import React, { useEffect, useState, useCallback } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, ToastMain, LoadingDiv } from "../master/Master";
import { imgBaseUrl } from "../../api/api";
import Swal from "sweetalert2";
import StaffLayout from "./StaffLayout";

import { useLocation, Link } from "react-router-dom";

import * as XLSX from "xlsx";

import {
  staffReportSummary,
  staffReportDetail,
  staffSalaryPay, // reused as-is from StaffSalaryPay.jsx (staff_salary_pay_v2)
  salaryGeneratePreview,
  salaryGenerateSave,
  salaryPreviewByMonth,
  salaryGeneratedUpdate,
  salaryGeneratedRegenerate,
  staffSalaryBulkPay,
} from "../../api/Staff";

import "./StaffReportDashboard.css";
import usePrivateLink from "../config/usePrivateLink";

// ─── Helpers ────────────────────────────────────────────────────────────────
const rupee = (n) =>
  "₹" +
  parseFloat(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusBadge = (status) => {
  const map = {
    Paid: "bg-label-success",
    Partial: "bg-label-warning",
    Due: "bg-label-danger",
    Generated: "bg-label-info",
    "Not Generated": "bg-label-secondary",
    Advance: "bg-label-primary",
  };
  return (
    <span
      className={`badge ${map[status] || "bg-label-secondary"} rounded-pill px-3`}
    >
      {status}
    </span>
  );
};

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((x) => x[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

const monthNames = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Default the generator to LAST month (the month that's actually due),
// same convention the old cron used.
const getDefaultGenMonthYear = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

// ────────────────────────────────────────────────────────────────────────────
export default function StaffReportDashboard() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const defaultReportDate = getDefaultGenMonthYear();

  const [year, setYear] = useState(defaultReportDate.year);
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState(String(defaultReportDate.month));
  const [staffType, setStaffType] = useState("");

  // Tier 1 — summary dashboard
  const [staffRows, setStaffRows] = useState([]);
  const [summary, setSummary] = useState(null);

  // Bulk salary payment for the month selected in the dashboard filter.
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [bulkPayLoading, setBulkPayLoading] = useState(false);
  const [bulkBanks, setBulkBanks] = useState([]);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkAmounts, setBulkAmounts] = useState({});
  const [bulkPayForm, setBulkPayForm] = useState({
    debit_account: "",
    payment_date: new Date().toISOString().split("T")[0],
    remark: "",
  });

  // Tier 2 — selected staff detail
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [detail, setDetail] = useState(null); // { data, employment, months, summary, current_advance, banks }
  const [checkedIds, setCheckedIds] = useState([]);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [payForm, setPayForm] = useState({
    debit_account: "",
    payment_date: new Date().toISOString().split("T")[0],
    remark: "",
  });

  // Advance (current month, not yet generated) mini-form
  const [advanceAmount, setAdvanceAmount] = useState("");

  // Collapsed-by-default panel for months that haven't been generated yet
  const [showNotGenerated, setShowNotGenerated] = useState(false);

  // ── Generate Salary modal state ─────────────────────────────────────────
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [genStep, setGenStep] = useState("pick"); // 'pick' | 'preview'
  const [genLoading, setGenLoading] = useState(false);
  const defaultGen = getDefaultGenMonthYear();
  const [genMonth, setGenMonth] = useState(defaultGen.month);
  const [genYear, setGenYear] = useState(defaultGen.year);
  const [previewRows, setPreviewRows] = useState([]);
  const [selectedPreviewStaffIds, setSelectedPreviewStaffIds] = useState([]);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [payIncompleteAttendance, setPayIncompleteAttendance] = useState(false);
  const [salaryCalculationMode, setSalaryCalculationMode] = useState("calendar_days");

  // Generated salary correction / regeneration
  const [editSalary, setEditSalary] = useState(null);
  const [editSalaryLoading, setEditSalaryLoading] = useState(false);

  // ── Salary Calendar ────────────────────────────────────────────────────────
  const defaultCalendar = getDefaultGenMonthYear();

  const [showSalaryCalendar, setShowSalaryCalendar] = useState(false);
  const [monthSalaryLoading, setMonthSalaryLoading] = useState(false);
  const [monthSalaryPreview, setMonthSalaryPreview] = useState(null);

  const [calendarMonth, setCalendarMonth] = useState(defaultCalendar.month);
  const [calendarYear, setCalendarYear] = useState(defaultCalendar.year);

  const [calendarStaff, setCalendarStaff] = useState(null);
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

  // Keep the incomplete-attendance payroll rule separate for the Salary Calendar.
  // IMPORTANT: opening an already-generated salary must not use the current
  // Generate Salary toggle, because that may be different from the rule used
  // when that payroll was originally generated.
  const [calendarPayIncompleteAttendance, setCalendarPayIncompleteAttendance] = useState(false);

  // Financial/attendance summary snapshot for the exact row that opened the
  // calendar. This prevents the Salary Calendar from showing a fresh
  // recalculation when the table is displaying an already-generated payroll
  // snapshot or a manually-adjusted Generate Salary preview.
  const [calendarSalarySnapshot, setCalendarSalarySnapshot] = useState(null);

  const location = useLocation();

  // ── Fetch dashboard summary ─────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await staffReportSummary(
        `year=${year}&search=${search}&month=${month}&staff_type=${staffType}`,
      );
      if (res?.data?.status) {
        setStaffRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setBulkBanks(res.data.banks || []);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [year, search, month, staffType]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (location.state?.staffId) {
      setSelectedStaffId(location.state.staffId);
      fetchDetail(location.state.staffId, year);
    }
  }, [location.state]);

  // ── Fetch staff detail ──────────────────────────────────────────────────
  const fetchDetail = async (staffId, y = year) => {
    setLoading(true);
    setCheckedIds([]);
    setUseCustomAmount(false);
    setCustomAmount("");
    setAdvanceAmount("");
    setShowNotGenerated(false);
    try {
      const res = await staffReportDetail(`staff_id=${staffId}&year=${y}`);
      if (res?.data?.status) {
        setDetail(res.data);
        setPayForm((p) => ({
          ...p,
          debit_account: res.data.banks?.[0]?.account_no || "",
        }));
      } else {
        SweetAlertShow(
          "info",
          "Notice",
          res?.data?.message || "No report data found.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStaff = (staff) => {
    setSelectedStaffId(staff.staff_id);
    fetchDetail(staff.staff_id, year);
  };

  const handleBackToDashboard = () => {
    setSelectedStaffId(null);
    setDetail(null);
  };

  // When year changes while a staff detail is open, refresh it too.
  useEffect(() => {
    if (selectedStaffId) fetchDetail(selectedStaffId, year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // ── Split months: generated (shown in main table) vs not-generated (collapsed) ──
  const generatedMonths = (detail?.months || []).filter((m) => m.is_generated);

  // Latest generated payroll is the only paid payroll that may still be corrected.
  // Any unpaid/partially-paid payroll remains editable.
  const latestGeneratedMonth = generatedMonths.reduce((latest, row) => {
    if (!latest) return row;
    const rowKey = Number(row.year) * 100 + Number(row.month);
    const latestKey = Number(latest.year) * 100 + Number(latest.month);
    return rowKey > latestKey ? row : latest;
  }, null);

  const canEditGeneratedSalary = (row) => {
    const paid = Number(row?.paid_amount || 0);
    if (paid <= 0) return true;
    if (!latestGeneratedMonth) return false;
    return (
      Number(row.year) === Number(latestGeneratedMonth.year) &&
      Number(row.month) === Number(latestGeneratedMonth.month)
    );
  };
  const notGeneratedMonths = (detail?.months || []).filter(
    (m) => !m.is_generated,
  );

  // ── Checkbox handling (only rows that are actually payable/generated) ──
  const payableMonths = (detail?.months || []).filter(
    (m) => m.payable && m.pending_amount > 0 && m.salary_id,
  );

  const toggleCheck = (id) =>
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setCheckedIds(
      checkedIds.length === payableMonths.length
        ? []
        : payableMonths.map((m) => m.salary_id),
    );

  const selectedRows = payableMonths.filter((m) =>
    checkedIds.includes(m.salary_id),
  );
  const totalSelectedPending = selectedRows.reduce(
    (acc, m) => acc + m.pending_amount,
    0,
  );
  const normalPayAmount = useCustomAmount
    ? parseFloat(customAmount) || 0
    : totalSelectedPending;

  // ── Pay submit (normal, month-wise) ─────────────────────────────────────
  const handlePay = async (e) => {
    e.preventDefault();
    if (!detail?.data) return;
    if (!payForm.debit_account)
      return SweetAlertShow("info", "Info", "Select a bank account.");
    if (checkedIds.length === 0)
      return SweetAlertShow(
        "info",
        "Info",
        "Select at least one month to pay.",
      );
    if (normalPayAmount <= 0)
      return SweetAlertShow("info", "Info", "Enter a valid amount.");

    const ok = await Swal.fire({
      title: "Confirm Payment",
      html: `Pay <b>${rupee(normalPayAmount)}</b> for <b>${selectedRows
        .map((s) => s.month_label)
        .join(", ")}</b> to <b>${detail.data.name}</b>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Pay Now",
      cancelButtonText: "Cancel",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("staff_id", detail.data.id);
      fd.append("paid_amount", normalPayAmount);
      fd.append("debit_account", payForm.debit_account);
      fd.append("payment_date", payForm.payment_date);
      fd.append("remark", payForm.remark);
      fd.append("payment_type", "normal");
      fd.append("salary_ids", JSON.stringify(checkedIds));

      const res = await staffSalaryPay(fd);
      if (res?.data?.status) {
        ToastMain.fire({ icon: "success", title: res.data.message });
        fetchDetail(detail.data.id, year);
        fetchSummary();
        setPayForm((p) => ({ ...p, remark: "" }));
        setCustomAmount("");
        setUseCustomAmount(false);
      } else {
        SweetAlertShow("error", "Failed", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Advance pay (current month, not yet generated) ──────────────────────
  const handleAdvancePay = async (e) => {
    e.preventDefault();
    if (!detail?.data) return;
    if (!payForm.debit_account)
      return SweetAlertShow("info", "Info", "Select a bank account.");
    const amount = parseFloat(advanceAmount) || 0;
    if (amount <= 0)
      return SweetAlertShow("info", "Info", "Enter a valid amount.");

    const ok = await Swal.fire({
      title: "Confirm Advance",
      html: `Pay advance <b>${rupee(amount)}</b> to <b>${detail.data.name}</b> for the current month?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Pay Now",
      cancelButtonText: "Cancel",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("staff_id", detail.data.id);
      fd.append("paid_amount", amount);
      fd.append("debit_account", payForm.debit_account);
      fd.append("payment_date", payForm.payment_date);
      fd.append("remark", payForm.remark);
      fd.append("payment_type", "advance");

      const res = await staffSalaryPay(fd);
      if (res?.data?.status) {
        ToastMain.fire({ icon: "success", title: res.data.message });
        fetchDetail(detail.data.id, year);
        fetchSummary();
        setAdvanceAmount("");
      } else {
        SweetAlertShow("error", "Failed", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Generate Salary: open modal ─────────────────────────────────────────
  const openGenerateModal = () => {
    const d = getDefaultGenMonthYear();
    setGenMonth(d.month);
    setGenYear(d.year);
    setGenStep("pick");
    setPreviewRows([]);
    setSelectedPreviewStaffIds([]);
    setPreviewSummary(null);
    setShowGenerateModal(true);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setGenStep("pick");
    setPreviewRows([]);
    setSelectedPreviewStaffIds([]);
    setPreviewSummary(null);
  };

  // ── Generate Salary: fetch preview (NOT saved yet) ──────────────────────
  const handleGeneratePreview = async (incompleteOverride = null) => {
    setGenLoading(true);
    try {
      const incompletePay =
        incompleteOverride === null
          ? payIncompleteAttendance
          : Boolean(incompleteOverride);

      const res = await salaryGeneratePreview({
        month: genMonth,
        year: genYear,
        pay_incomplete_attendance: incompletePay ? 1 : 0,
        calculation_mode: salaryCalculationMode,
      });
      if (res?.data?.status) {
        // Laravel returns the applied incomplete-attendance flag at response root.
        // Prefer that server-confirmed value over local React state.
        const appliedIncompletePay =
          res.data.pay_incomplete_attendance === true ||
            res.data.pay_incomplete_attendance === 1 ||
            res.data.pay_incomplete_attendance === "1"
            ? true
            : res.data.pay_incomplete_attendance === false ||
              res.data.pay_incomplete_attendance === 0 ||
              res.data.pay_incomplete_attendance === "0"
              ? false
              : Boolean(incompletePay);

        setPayIncompleteAttendance(appliedIncompletePay);

        const rows = (res.data.data || []).map((row) => ({
          ...row,
          // Freeze the exact incomplete-attendance choice used for THIS preview.
          // This prevents the Salary Calendar from reading a stale/global toggle value.
          __pay_incomplete_attendance: appliedIncompletePay,
          __server_present_days: Number(row.present_days || 0),
          __server_earned_salary: Number(row.earned_salary || 0),
          __server_deduction: Number(row.deduction || 0),
          __server_per_day_salary: Number(row.per_day_salary || 0),
          __server_salary_divisor: Number(row.salary_divisor || 0),
          __manual_adjusted: false,
        }));
        setPreviewRows(rows);
        setSelectedPreviewStaffIds(
          rows.filter((row) => !row.skippable).map((row) => row.staff_id),
        );
        setPreviewSummary(res.data.summary || null);
        setGenStep("preview");
      } else {
        SweetAlertShow("error", "Failed", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setGenLoading(false);
    }
  };

  const handleIncompleteSalaryToggle = async () => {
    const nextValue = !payIncompleteAttendance;
    setPayIncompleteAttendance(nextValue);

    // Recalculate the complete preview from the backend immediately so all
    // employee totals, deduction cards and calendar breakdowns stay accurate.
    if (genStep === "preview") {
      await handleGeneratePreview(nextValue);
    }
  };

  // ── Generate Salary: edit a row before saving ───────────────────────────
  // The backend preview is the audit/base calculation. Manual Present Days
  // adjustments move salary by exactly one selected per-day amount per day.
  // This preserves server-calculated half-day, leave, low-time and incomplete
  // effects instead of rebuilding payroll differently in React.
  const updatePreviewRow = (staffId, field, rawValue) => {
    setPreviewRows((rows) =>
      rows.map((r) => {
        if (r.staff_id !== staffId) return r;

        const updated = { ...r };
        const base = Math.max(0, Number(r.base_salary || 0));
        const divisor = Math.max(
          1,
          Number(
            r.salary_divisor ||
            (salaryCalculationMode === "working_days"
              ? r.working_days
              : r.month_days) ||
            1,
          ),
        );
        // Use the exact server per-day value when available. Do not re-round it
        // before calculations; round only the final money fields.
        const perDay = Math.max(
          0,
          Number(
            r.__server_per_day_salary ||
            r.per_day_salary ||
            (base > 0 ? base / divisor : 0),
          ),
        );
        const bonus = Math.max(0, Number(r.bonus || 0));

        if (field === "present_days") {
          if (rawValue === "") {
            updated.present_days = "";
            return updated;
          }

          const maxPresent = Math.max(
            0,
            Number(
              salaryCalculationMode === "working_days"
                ? r.working_days || 0
                : r.month_days || 0,
            ),
          );
          const nextPresent = Math.min(
            maxPresent,
            Math.max(0, Number(rawValue || 0)),
          );

          // Fixed-monthly mode is intentionally attendance-independent.
          if (salaryCalculationMode === "fixed_monthly") {
            updated.present_days = nextPresent;
            updated.earned_salary = Number(base.toFixed(2));
            updated.deduction = 0;
            updated.net_salary = Number((base + bonus).toFixed(2));
            updated.__manual_adjusted = true;
            return updated;
          }

          const serverPresent = Number(
            r.__server_present_days ?? r.present_days ?? 0,
          );
          const serverEarned = Number(
            r.__server_earned_salary ?? r.earned_salary ?? 0,
          );

          // One manually added/removed present day is worth exactly one day
          // under the selected divisor:
          // calendar_days => base / 28|29|30|31
          // working_days  => base / scheduled working days
          const attendanceDelta = (nextPresent - serverPresent) * perDay;
          const nextEarned = Math.min(
            base,
            Math.max(0, serverEarned + attendanceDelta),
          );
          const nextDeduction = Math.max(0, base - nextEarned);

          updated.present_days = nextPresent;
          updated.earned_salary = Number(nextEarned.toFixed(2));
          updated.deduction = Number(nextDeduction.toFixed(2));
          updated.net_salary = Number(
            Math.max(0, nextEarned + bonus).toFixed(2),
          );
          updated.__manual_adjusted = true;
          return updated;
        }

        if (field === "deduction") {
          if (rawValue === "") {
            updated.deduction = "";
            return updated;
          }

          const nextDeduction = Math.min(
            base,
            Math.max(0, Number(rawValue || 0)),
          );
          const nextEarned = Math.max(0, base - nextDeduction);

          updated.deduction = Number(nextDeduction.toFixed(2));
          updated.earned_salary = Number(nextEarned.toFixed(2));
          updated.net_salary = Number(
            Math.max(0, nextEarned + bonus).toFixed(2),
          );
          updated.__manual_adjusted = true;
          return updated;
        }

        return { ...updated, [field]: rawValue };
      }),
    );
  };

  const resetPreviewRow = (staffId) => {
    setPreviewRows((rows) =>
      rows.map((r) => {
        if (r.staff_id !== staffId) return r;
        const earned = Number(r.__server_earned_salary ?? r.earned_salary ?? 0);
        const deduction = Number(r.__server_deduction ?? r.deduction ?? 0);
        const bonus = Math.max(0, Number(r.bonus || 0));
        return {
          ...r,
          present_days: Number(r.__server_present_days ?? r.present_days ?? 0),
          earned_salary: earned,
          deduction,
          net_salary: Number(Math.max(0, earned + bonus).toFixed(2)),
          __manual_adjusted: false,
        };
      }),
    );
  };

  // ── Generate Salary: save (persist to DB) ───────────────────────────────
  const handleGenerateSave = async () => {
    // Sanitize every row: force every numeric field to a real, non-negative
    // number so nothing can slip through as undefined/NaN/empty string.
    const sanitizeEntry = (r) => ({
      staff_id: r.staff_id,
      month: r.month,
      year: r.year,
      month_days: r.month_days,
      working_days: Number(r.working_days) || 0,
      present_days: Number(r.present_days) || 0,
      half_days: Number(r.half_days) || 0,
      absent_days: Number(r.absent_days) || 0,
      leave_days: Number(r.leave_days) || 0,
      paid_leaves: Number(r.paid_leaves) || 0,
      unpaid_leave_days: Number(r.unpaid_leave_days) || 0,
      carry_days: Number(r.carry_days) || 0,
      overtime: Number(r.overtime) || 0,
      base_salary: Number(r.base_salary) || 0,
      overtime_rate: Number(r.overtime_rate) || 0,
      earned_salary: Number(r.earned_salary) || 0,
      deduction: Number(r.deduction) || 0,
      bonus: Number(r.bonus) || 0,
      net_salary: Number(r.net_salary) || 0,
      calculation_mode: salaryCalculationMode,
    });

    const entries = previewRows
      .filter((r) => !r.skippable && selectedPreviewStaffIds.includes(r.staff_id))
      .map(sanitizeEntry);

    if (entries.length === 0) {
      return SweetAlertShow(
        "info",
        "Info",
        "Please select at least one staff member to generate salary.",
      );
    }

    const ok = await Swal.fire({
      title: "Confirm Generate & Save",
      html: `Generate and save salary for <b>${entries.length}</b> staff for <b>${monthNames[genMonth]} ${genYear}</b>?<br/><small class="text-muted">This will be written to the database and cannot be undone from here.</small>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
    });
    if (!ok?.isConfirmed) return;

    setGenLoading(true);
    try {
      const res = await salaryGenerateSave({
        entries,
        pay_incomplete_attendance: payIncompleteAttendance ? 1 : 0,
        calculation_mode: salaryCalculationMode,
      });
      if (res?.data?.status) {
        ToastMain.fire({ icon: "success", title: res.data.message });
        closeGenerateModal();
        fetchSummary();
        if (selectedStaffId) fetchDetail(selectedStaffId, year);
      } else {
        SweetAlertShow("error", "Failed", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setGenLoading(false);
    }
  };

  // ── Generated Salary: manual correction ──────────────────────────────────
  const openGeneratedSalaryEdit = (row) => {
    setEditSalary({
      salary_id: row.salary_id,
      month_label: row.month_label,
      base_salary: Number(row.base_salary || 0),
      earned_salary: Number(row.earned_salary || 0),
      deduction: Number(row.deduction || 0),
      bonus: Number(row.overtime_bonus || 0),
      net_salary: Number(row.net_salary || 0),
      paid_amount: Number(row.paid_amount || 0),
      working_days: Number(row.total_working_days || 0),
      present_days: Number(row.present_days || 0),
      half_days: Number(row.half_days || 0),
      leave_days: Number(row.leave_days || 0),
      remark: row.remark || "",
    });
  };

  const changeGeneratedSalaryEdit = (field, value) => {
    setEditSalary((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      if (["earned_salary", "bonus"].includes(field)) {
        const earned = Math.max(0, Number(next.earned_salary || 0));
        const bonus = Math.max(0, Number(next.bonus || 0));
        next.net_salary = Number((earned + bonus).toFixed(2));
        next.deduction = Number(
          Math.max(0, Number(next.base_salary || 0) - earned).toFixed(2),
        );
      }
      return next;
    });
  };

  const saveGeneratedSalaryEdit = async () => {
    if (!editSalary?.salary_id) return;
    const net = Number(editSalary.net_salary || 0);
    const paid = Number(editSalary.paid_amount || 0);
    if (net < paid) {
      return SweetAlertShow(
        "info",
        "Invalid Salary",
        `Net salary cannot be less than already paid amount ${rupee(paid)}.`,
      );
    }

    const ok = await Swal.fire({
      title: "Update Generated Salary?",
      html: `Update <b>${editSalary.month_label}</b> salary to <b>${rupee(net)}</b>?<br/><small class="text-muted">This is a manual payroll correction. Paid amount will not be changed.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
    });
    if (!ok?.isConfirmed) return;

    setEditSalaryLoading(true);
    try {
      const res = await salaryGeneratedUpdate({
        salary_id: editSalary.salary_id,
        earned_salary: Number(editSalary.earned_salary || 0),
        bonus: Number(editSalary.bonus || 0),
        working_days: Number(editSalary.working_days || 0),
        present_days: Number(editSalary.present_days || 0),
        half_days: Number(editSalary.half_days || 0),
        leave_days: Number(editSalary.leave_days || 0),
        remark: editSalary.remark || "",
      });
      if (!res?.data?.status) throw new Error(res?.data?.message || "Update failed");
      ToastMain.fire({ icon: "success", title: res.data.message });
      setEditSalary(null);
      await fetchDetail(selectedStaffId, year);
      await fetchSummary();
    } catch (err) {
      SweetAlertShow("error", "Error", err?.response?.data?.message || err?.message);
    } finally {
      setEditSalaryLoading(false);
    }
  };

  const regenerateGeneratedSalary = async (row) => {
    const ok = await Swal.fire({
      title: `Regenerate ${row.month_label}?`,
      html: `This will recalculate attendance, deductions and salary using the <b>schedule snapshot saved with this payroll</b>.<br/><small class="text-danger">Already-paid payroll cannot be regenerated.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Regenerate",
      confirmButtonColor: "#d33",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const regenerateIncompleteRule = resolveIncompleteSalaryRule(row, false);

      const res = await salaryGeneratedRegenerate({
        salary_id: row.salary_id,
        // Regeneration of an existing payroll must keep that payroll's own
        // incomplete-attendance rule; do not reuse the Generate Salary toggle.
        pay_incomplete_attendance: regenerateIncompleteRule ? 1 : 0,
      });
      if (!res?.data?.status) throw new Error(res?.data?.message || "Regeneration failed");
      ToastMain.fire({ icon: "success", title: res.data.message });
      await fetchDetail(selectedStaffId, year);
      await fetchSummary();
    } catch (err) {
      SweetAlertShow("error", "Error", err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Salary Calendar: fetch month data ─────────────────────────────────────
  // Resolve the rule that belongs to the payroll being viewed.
  // Supports current API fields plus snapshot-shaped responses.
  const resolveIncompleteSalaryRule = (source, fallback = false) => {
    const candidates = [
      source?.__pay_incomplete_attendance,
      source?.pay_incomplete_attendance,
      source?.incomplete_salary_applied,
      source?.incomplete_attendance_paid,
      source?.pay_incomplete,
      source?.salary_pay_incomplete_attendance,
      source?.schedule_snapshot?.pay_incomplete_attendance,
      source?.payroll_snapshot?.pay_incomplete_attendance,
    ];

    for (const value of candidates) {
      if (value === true || value === 1 || value === "1") return true;
      if (value === false || value === 0 || value === "0") return false;
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["yes", "true", "on", "paid"].includes(normalized)) return true;
        if (["no", "false", "off", "deducted"].includes(normalized)) return false;
      }
    }

    return Boolean(fallback);
  };

  const firstFiniteNumber = (...values) => {
    for (const value of values) {
      if (value === undefined || value === null || value === "") continue;
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const buildCalendarSalarySnapshot = (
    row,
    selectedMonth,
    selectedYear,
    source = "report",
  ) => {
    if (!row) return null;

    const isGenerationPreview = source === "generation_preview";
    const hasGeneratedSalary = Boolean(
      row?.salary_id ||
      row?.is_generated ||
      row?.already_generated ||
      row?.month_net_salary !== undefined,
    );

    // A dashboard row without a month filter contains YEAR totals. Never use
    // those year totals as a one-month calendar snapshot.
    if (!isGenerationPreview && !hasGeneratedSalary) return null;

    const base = firstFiniteNumber(
      row?.month_base_salary,
      row?.base_salary,
    );

    const earned = firstFiniteNumber(
      row?.month_earned_salary,
      row?.earned_salary,
      row?.month_net_salary,
      row?.net_salary,
      row?.total_earned,
    );

    const bonus = firstFiniteNumber(
      row?.month_bonus,
      row?.overtime_bonus,
      row?.bonus,
      0,
    );

    const net = firstFiniteNumber(
      row?.month_net_salary,
      row?.net_salary,
      row?.total_earned,
      earned !== null ? earned + (bonus || 0) : null,
    );

    const deduction = firstFiniteNumber(
      row?.month_deduction,
      row?.deduction,
      base !== null && earned !== null ? Math.max(0, base - earned) : null,
    );

    return {
      source,
      salary_id: row?.salary_id ?? null,
      month: Number(selectedMonth || row?.month || row?.selected_month || 0) || null,
      year: Number(selectedYear || row?.year || row?.selected_year || 0) || null,
      is_generated: hasGeneratedSalary,
      base_salary: base,
      earned_salary: earned,
      deduction,
      bonus,
      net_salary: net,
      working_days: firstFiniteNumber(
        row?.month_working_days,
        row?.working_days,
        row?.total_working_days,
      ),
      present_days: firstFiniteNumber(
        row?.month_present_days,
        row?.present_days,
      ),
      half_days: firstFiniteNumber(
        row?.month_half_days,
        row?.half_days,
      ),
      absent_days: firstFiniteNumber(
        row?.month_absent_days,
        row?.absent_days,
      ),
      leave_days: firstFiniteNumber(
        row?.month_leave_days,
        row?.leave_days,
      ),
      paid_leaves: firstFiniteNumber(
        row?.month_paid_leaves,
        row?.paid_leaves,
      ),
      overtime_hours: firstFiniteNumber(
        row?.month_overtime_hours,
        row?.overtime_hours,
        row?.overtime,
      ),
      paid_amount: firstFiniteNumber(
        row?.month_paid_amount,
        row?.paid_amount,
        row?.total_paid,
      ),
      pending_amount: firstFiniteNumber(
        row?.month_pending_amount,
        row?.pending_amount,
        row?.total_pending,
      ),
      pay_incomplete_attendance:
        row?.pay_incomplete_attendance ??
        row?.incomplete_attendance_paid ??
        row?.pay_incomplete ??
        row?.schedule_snapshot?.pay_incomplete_attendance ??
        row?.payroll_snapshot?.pay_incomplete_attendance,
    };
  };

  const mergeCalendarSalarySnapshot = (apiRow, snapshot) => {
    if (!snapshot) return apiRow;

    const summaryFields = [
      "salary_id",
      "base_salary",
      "earned_salary",
      "deduction",
      "bonus",
      "net_salary",
      "working_days",
      "present_days",
      "half_days",
      "absent_days",
      "leave_days",
      "paid_leaves",
      "overtime_hours",
      "paid_amount",
      "pending_amount",
    ];

    const merged = { ...apiRow };

    summaryFields.forEach((key) => {
      const value = snapshot?.[key];
      if (value !== undefined && value !== null && value !== "") {
        merged[key] = value;
      }
    });

    // Keep API day-wise attendance. Only the month summary is frozen to the
    // exact row/preview the user clicked.
    merged.attendance = apiRow?.attendance || [];
    merged.__calendar_snapshot_source = snapshot.source;
    merged.__calendar_snapshot_applied = true;

    return merged;
  };

  const fetchSalaryCalendar = async (
    staffId,
    selectedMonth,
    selectedYear,
    incompleteSalaryRule = false,
    salarySnapshot = null,
  ) => {
    setMonthSalaryLoading(true);

    try {
      const res = await salaryPreviewByMonth({
        staff_id: staffId,
        month: selectedMonth,
        year: selectedYear,
        pay_incomplete_attendance: incompleteSalaryRule ? 1 : 0,
        calculation_mode: salaryCalculationMode,
      });

      if (res?.data?.status) {
        const staffData = res.data?.data?.[0] || null;

        if (staffData) {
          // Keep the rule used for this API calculation together with the returned
          // salary data.  The UI now reads this snapshot instead of a global toggle.
          // salary_preview_by_month returns `pay_incomplete_attendance` at the
          // response root. Use it first, then staff data, then the request value.
          const resolvedRule = resolveIncompleteSalaryRule(
            res.data,
            resolveIncompleteSalaryRule(
              staffData,
              Boolean(incompleteSalaryRule),
            ),
          );

          const normalizedStaffData = {
            ...staffData,
            __pay_incomplete_attendance: resolvedRule,
          };

          // If this calendar was opened from a generated/report row (or from an
          // edited generation preview), keep the exact month summary shown in
          // that source row. The API attendance array is still used for the
          // day-wise calendar.
          const mergedStaffData = mergeCalendarSalarySnapshot(
            normalizedStaffData,
            salarySnapshot,
          );

          setCalendarPayIncompleteAttendance(resolvedRule);
          setMonthSalaryPreview(mergedStaffData);
        } else {
          setMonthSalaryPreview(null);

          SweetAlertShow(
            "info",
            "No Data",
            "No salary data found for this month.",
          );
        }
      } else {
        setMonthSalaryPreview(null);

        SweetAlertShow(
          "error",
          "Failed",
          res?.data?.message || "Unable to calculate salary.",
        );
      }
    } catch (err) {
      setMonthSalaryPreview(null);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ||
        err?.message ||
        "Unable to calculate salary.",
      );
    } finally {
      setMonthSalaryLoading(false);
    }
  };

  // ── Open Salary Calendar ───────────────────────────────────────────────────
  const openSalaryCalendar = async (
    staff,
    selectedMonth = null,
    selectedYear = null,
    options = {},
  ) => {
    const defaultDate = getDefaultGenMonthYear();

    const monthToUse = selectedMonth || defaultDate.month;
    const yearToUse = selectedYear || defaultDate.year;

    // Generation-preview calendar must follow the live Generate Salary toggle.
    // Generated salary details must follow the rule saved/returned with that row.
    const isGenerationPreview = options?.source === "generation_preview";
    const incompleteRule = isGenerationPreview
      ? resolveIncompleteSalaryRule(staff, Boolean(payIncompleteAttendance))
      : resolveIncompleteSalaryRule(staff, false);

    const salarySnapshot =
      options?.salarySnapshot ||
      buildCalendarSalarySnapshot(
        staff,
        monthToUse,
        yearToUse,
        isGenerationPreview ? "generation_preview" : "report",
      );

    setCalendarPayIncompleteAttendance(incompleteRule);
    setCalendarSalarySnapshot(salarySnapshot);

    setCalendarStaff({
      staff_id: staff.staff_id,
      staff_name: staff.staff_name || staff.name,
      staff_img: staff.staff_img || staff.img,
    });

    setCalendarMonth(monthToUse);
    setCalendarYear(yearToUse);
    setSelectedCalendarDay(null);
    setMonthSalaryPreview(null);
    setShowSalaryCalendar(true);

    await fetchSalaryCalendar(
      staff.staff_id,
      monthToUse,
      yearToUse,
      incompleteRule,
      salarySnapshot,
    );
  };

  // ── Change Calendar Month ──────────────────────────────────────────────────
  const changeCalendarMonth = (direction) => {
    let nextMonth = calendarMonth + direction;
    let nextYear = calendarYear;

    if (nextMonth < 1) {
      nextMonth = 12;
      nextYear--;
    }

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear++;
    }

    setCalendarMonth(nextMonth);
    setCalendarYear(nextYear);
    setSelectedCalendarDay(null);

    // The row snapshot belongs only to the month that opened the calendar.
    // When navigating to another month, never reuse those amounts.
    setCalendarSalarySnapshot(null);

    if (calendarStaff?.staff_id) {
      fetchSalaryCalendar(
        calendarStaff.staff_id,
        nextMonth,
        nextYear,
        calendarPayIncompleteAttendance,
        null,
      );
    }
  };

  // ── Close Salary Calendar ──────────────────────────────────────────────────
  const closeSalaryCalendar = () => {
    if (monthSalaryLoading) return;

    setShowSalaryCalendar(false);
    setMonthSalaryPreview(null);
    setCalendarStaff(null);
    setSelectedCalendarDay(null);
    setCalendarPayIncompleteAttendance(false);
    setCalendarSalarySnapshot(null);
  };

  // ── Calendar status class ──────────────────────────────────────────────────
  const getCalendarStatusClass = (status) => {
    const normalizedStatus = String(status || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]/g, " ");

    switch (normalizedStatus) {
      case "present":
        return "salary-calendar-present";

      case "half day":
      case "half":
        return "salary-calendar-half";

      case "low time":
      case "partial":
        return "salary-calendar-half";

      case "absent":
        return "salary-calendar-absent";

      case "leave":
      case "paid leave":
        return "salary-calendar-leave";

      case "unpaid leave":
        return "salary-calendar-absent";

      case "holiday":
        return "salary-calendar-holiday";

      case "weekly off":
      case "sunday":
        return "salary-calendar-weekly-off";

      default:
        return "salary-calendar-other";
    }
  };

  const getCalendarDisplayStatus = (dayData, calendarDate = null) => {
    if (!dayData && !calendarDate) return "No Data";

    const normalizeCalendarValue = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]/g, " ");

    const attendanceType = normalizeCalendarValue(dayData?.attendance_type);
    const status = normalizeCalendarValue(dayData?.status);
    const dayType = normalizeCalendarValue(
      dayData?.day_type || dayData?.type || dayData?.calendar_type,
    );

    const hasAttendance = Boolean(dayData?.check_in || dayData?.check_out);
    const isSunday = calendarDate instanceof Date && calendarDate.getDay() === 0;

    const isHoliday =
      dayData?.is_holiday === true ||
      dayData?.is_holiday === 1 ||
      dayData?.is_holiday === "1" ||
      status === "holiday" ||
      attendanceType === "holiday" ||
      dayType === "holiday" ||
      Boolean(dayData?.holiday_name);

    const isWeeklyOff =
      dayData?.is_weekly_off === true ||
      dayData?.is_weekly_off === 1 ||
      dayData?.is_weekly_off === "1" ||
      status === "weekly off" ||
      attendanceType === "weekly off" ||
      dayType === "weekly off";

    // If the employee actually worked, show the attendance result first.
    // This keeps worked Sundays/holidays visible as attendance days.
    if (hasAttendance) {
      if (attendanceType === "low time" || attendanceType === "partial") {
        return "Low Time";
      }

      if (
        attendanceType === "half day" ||
        attendanceType === "half" ||
        status === "half day" ||
        status === "half"
      ) {
        return "Half Day";
      }

      if (status === "present" || attendanceType === "present") {
        return "Present";
      }
    }

    // IMPORTANT: non-working calendar days take priority over leave/absence.
    // Sunday must always be labelled Sunday when there is no attendance.
    if (isSunday) {
      return "Sunday";
    }

    if (isHoliday) {
      return "Holiday";
    }

    if (isWeeklyOff) {
      return "Weekly Off";
    }

    if (status === "paid leave" || attendanceType === "paid leave") {
      return "Paid Leave";
    }

    if (status === "unpaid leave" || attendanceType === "unpaid leave") {
      return "Unpaid Leave";
    }

    if (status === "leave" || attendanceType === "leave") {
      return "Leave";
    }

    if (status === "absent" || attendanceType === "absent") {
      return "Absent";
    }

    if (status === "present" || attendanceType === "present") {
      return "Present";
    }

    return dayData?.status || dayData?.attendance_type || "No Data";
  };

  // Calendar only needs attendance timing, not day-wise salary money.
  const formatCalendarTime = (value) => {
    if (!value) return "—";

    const raw = String(value).trim();
    if (!raw) return "—";

    // Supports HH:mm / HH:mm:ss and datetime strings returned by the API.
    const timeMatch = raw.match(/(?:T|\s)?(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!timeMatch) return raw;

    let hour = Number(timeMatch[1]);
    const minute = timeMatch[2];

    if (Number.isNaN(hour)) return raw;

    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;

    return `${String(hour).padStart(2, "0")}:${minute} ${period}`;
  };

  const getCalendarDays = (year, month) => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);

    const startDay = firstDay.getDay(); // 0 = Sunday
    const totalDays = lastDay.getDate();

    const days = [];

    // Empty cells before day 1
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= totalDays; day++) {
      days.push(new Date(year, month - 1, day));
    }

    return days;
  };

  const payablePreviewRows = previewRows.filter((r) => !r.skippable);
  const selectedPayablePreviewRows = payablePreviewRows.filter((r) =>
    selectedPreviewStaffIds.includes(r.staff_id),
  );
  const allPayableSelected =
    payablePreviewRows.length > 0 &&
    selectedPayablePreviewRows.length === payablePreviewRows.length;

  const togglePreviewStaff = (staffId) => {
    setSelectedPreviewStaffIds((ids) =>
      ids.includes(staffId)
        ? ids.filter((id) => id !== staffId)
        : [...ids, staffId],
    );
  };

  const selectAllPreviewStaff = () =>
    setSelectedPreviewStaffIds(payablePreviewRows.map((r) => r.staff_id));

  const clearPreviewStaff = () => setSelectedPreviewStaffIds([]);
  // Resolve the TRUE incomplete-attendance DAY count from the salary preview.
  // Backend computeStaffMonthlySalary() returns day-wise rows in `attendance` and
  // marks incomplete days with `has_incomplete` / `incomplete_sessions`.
  // Count DAYS, not sessions, and keep this independent from whether incomplete
  // attendance is paid or deducted.
  const getIncompleteAttendanceCount = (row) => {
    const toNumber = (value) => {
      if (value === undefined || value === null || value === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
    };

    const getPunch = (day, type) => {
      const keys =
        type === "in"
          ? ["in_time", "check_in", "checkin", "punch_in", "time_in", "clock_in", "first_in"]
          : ["out_time", "check_out", "checkout", "punch_out", "time_out", "clock_out", "last_out"];

      for (const key of keys) {
        const value = day?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          return value;
        }
      }
      return null;
    };

    // IMPORTANT: current Laravel salary preview returns these rows as `attendance`.
    const dayRows = Array.isArray(row?.attendance)
      ? row.attendance
      : Array.isArray(row?.calendar)
        ? row.calendar
        : Array.isArray(row?.days)
          ? row.days
          : Array.isArray(row?.date_wise)
            ? row.date_wise
            : Array.isArray(row?.attendance_days)
              ? row.attendance_days
              : [];

    if (dayRows.length > 0) {
      const incompleteDates = new Set();

      dayRows.forEach((day, index) => {
        const status = String(
          day?.status ||
          day?.attendance_status ||
          day?.payroll_status ||
          day?.day_status ||
          day?.type ||
          "",
        ).toLowerCase();

        const hasIn = Boolean(getPunch(day, "in"));
        const hasOut = Boolean(getPunch(day, "out"));
        const incompleteSessions = toNumber(day?.incomplete_sessions) || 0;
        const explicitlyIncomplete =
          day?.has_incomplete === true ||
          day?.is_incomplete === true ||
          Number(day?.has_incomplete) === 1 ||
          Number(day?.is_incomplete) === 1 ||
          incompleteSessions > 0 ||
          status.includes("incomplete");

        // Exactly one punch is also incomplete. Both punches means complete unless
        // the backend explicitly reports another incomplete session for that day.
        const oneSidedPunch = hasIn !== hasOut;

        if (explicitlyIncomplete || oneSidedPunch) {
          const dateKey = String(
            day?.date || day?.attendance_date || day?.day || day?.work_date || `row-${index}`,
          );
          incompleteDates.add(dateKey);
        }
      });

      return incompleteDates.size;
    }

    // Fallback only for older API responses that do not include day-wise attendance.
    const directCandidates = [
      row?.incomplete_days,
      row?.incomplete_count,
      row?.incomplete_attendance_days,
      row?.attendance_summary?.incomplete_days,
      row?.attendance_summary?.incomplete,
      row?.summary?.incomplete_days,
      row?.summary?.incomplete,
    ];

    for (const candidate of directCandidates) {
      const count = toNumber(candidate);
      if (count !== null) return count;
    }

    return 0;
  };

  const skippedPreviewRows = previewRows.filter((r) => r.skippable);
  const previewTotalNet = payablePreviewRows.reduce(
    (acc, r) => acc + (parseFloat(r.net_salary) || 0),
    0,
  );

  // Live payroll totals. These recalculate immediately when an admin edits
  // earned salary in preview. Server summary is still kept as the audit source.
  const payrollTotals = payablePreviewRows.reduce(
    (t, r) => {
      const b = r.deduction_breakdown || {};
      t.base += Number(r.base_salary || 0);
      t.earned += Number(r.earned_salary || 0);
      t.net += Number(r.net_salary || 0);
      t.deduction += Number(r.deduction || 0);
      t.leave += Number(b.unpaid_leave || 0);
      t.absence += Number(b.absence || 0);
      t.lowTime += Number(b.low_time || 0) + Number(b.half_day || 0);
      t.incomplete += Number(b.incomplete_attendance || 0);
      return t;
    },
    { base: 0, earned: 0, net: 0, deduction: 0, leave: 0, absence: 0, lowTime: 0, incomplete: 0 },
  );

  const BankSelect = () => (
    <div className="form-floating form-floating-outline">
      <select
        className="form-select"
        value={payForm.debit_account}
        onChange={(e) =>
          setPayForm((p) => ({ ...p, debit_account: e.target.value }))
        }
        required
      >
        <option value="" disabled>
          Select Bank Account
        </option>
        {(detail?.banks || []).map((b) => (
          <option key={b.account_no} value={b.account_no}>
            A/C: {b.account_no} (Bal: ₹
            {parseFloat(b.amount).toLocaleString("en-IN")})
          </option>
        ))}
      </select>
      <label>Debit Bank Account</label>
    </div>
  );

  const YearSelect = () => (
    <select
      className="form-select w-auto"
      value={year}
      onChange={(e) => setYear(parseInt(e.target.value, 10))}
    >
      {yearOptions.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
    </select>
  );

  // ────────────────────────────────────────────────────────────────────────

  // ── Print only a specific table ───────────────────────────────────────
  const printTable = (tableId, title = "Report") => {
    const table = document.getElementById(tableId);

    if (!table) {
      console.error(`Table with ID "${tableId}" not found.`);
      return;
    }

    const printWindow = window.open("", "_blank", "width=1200,height=800");

    if (!printWindow) {
      alert("Please allow popups to print this report.");
      return;
    }

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>

      <style>
        * {
          box-sizing: border-box;
        }

        body {
          font-family: Arial, Helvetica, sans-serif;
          margin: 20px;
          color: #000;
          background: #fff;
        }

        .print-title {
          text-align: center;
          margin-bottom: 20px;
        }

        .print-title h2 {
          margin: 0 0 5px;
          font-size: 20px;
        }

        .print-title p {
          margin: 0;
          font-size: 12px;
          color: #555;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        th,
        td {
          border: 1px solid #999 !important;
          padding: 7px 8px;
          vertical-align: middle;
        }

        th {
          background: #f2f2f2 !important;
          font-weight: bold;
          color: #000 !important;
        }

        td {
          color: #000 !important;
        }

        .text-end {
          text-align: right !important;
        }

        .text-center {
          text-align: center !important;
        }

        .text-danger {
          color: #dc3545 !important;
        }

        .text-success {
          color: #198754 !important;
        }

        .text-primary {
          color: #0d6efd !important;
        }

        .text-warning {
          color: #856404 !important;
        }

        .badge {
          display: inline-block;
          padding: 3px 7px;
          border: 1px solid #999;
          border-radius: 10px;
          font-size: 10px;
        }

        img {
          max-width: 35px;
          max-height: 35px;
        }

        a {
          color: #000 !important;
          text-decoration: none !important;
        }

        input,
        button,
        .no-print {
          display: none !important;
        }

        @page {
          size: landscape;
          margin: 10mm;
        }

        @media print {
          body {
            margin: 0;
          }

          table {
            page-break-inside: auto;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }
        }
      </style>
    </head>

    <body>

      <div class="print-title">
        <h2>${title}</h2>
        <p>Printed on ${new Date().toLocaleString("en-IN")}</p>
      </div>

      ${table.outerHTML}

    </body>
    </html>
  `);

    printWindow.document.close();

    // Wait for images/styles to load
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  // ────────────────────────────────────────────────────────────────────────
  // Excel Export Helpers
  // ────────────────────────────────────────────────────────────────────────

  const downloadExcel = (data, fileName, sheetName = "Report") => {
    if (!data || data.length === 0) {
      SweetAlertShow(
        "info",
        "No Data",
        "There is no data available to export.",
      );
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);

    // Auto-size columns
    const columnWidths = [];

    data.forEach((row) => {
      Object.keys(row).forEach((key, index) => {
        const valueLength = String(row[key] ?? "").length;

        if (!columnWidths[index]) {
          columnWidths[index] = {
            wch: Math.min(Math.max(valueLength + 2, 12), 35),
          };
        } else {
          columnWidths[index].wch = Math.min(
            Math.max(columnWidths[index].wch, valueLength + 2),
            35,
          );
        }
      });
    });

    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    XLSX.writeFile(workbook, fileName);
  };

  const exportStaffReportExcel = () => {
    if (!staffRows || staffRows.length === 0) {
      SweetAlertShow(
        "info",
        "No Data",
        "There is no staff report data available to export.",
      );
      return;
    }

    const exportData = staffRows.map((s, index) => ({
      "S.No.": index + 1,
      "Staff Name": s.name || "",
      "Registration No.": s.registration_no || "",
      "Staff Type": s.staff_type || "",
      "Base Salary": Number(s.base_salary) || 0,
      "Total Earned": Number(s.total_earned) || 0,
      "Total Paid": Number(s.total_paid) || 0,
      "Pending Amount": Number(s.total_pending) || 0,
      "Pending Months": Number(s.pending_months) || 0,
      Status: s.status || "",
    }));

    const filterText = [
      search ? `Search-${search}` : "",
      staffType ? `Type-${staffType}` : "",
      month ? `Month-${monthNames[Number(month)]}` : "",
    ]
      .filter(Boolean)
      .join("_");

    const fileName = `Staff_Salary_Report_${year}${filterText ? `_${filterText}` : ""
      }.xlsx`;

    downloadExcel(exportData, fileName, "Staff Salary Report");
  };

  const exportSalaryPreviewExcel = () => {
    if (!previewRows || previewRows.length === 0) {
      SweetAlertShow(
        "info",
        "No Data",
        "There is no salary preview data available to export.",
      );
      return;
    }

    const exportData = previewRows.map((r, index) => ({
      "S.No.": index + 1,
      "Staff Name": r.staff_name || "",
      "Registration No.": r.registration_no || "",
      Month: monthNames[r.month] || monthNames[genMonth] || "",
      Year: r.year || genYear,

      Mode: r.mode === "fixed" ? "Fixed" : "Attendance",
      "Calculation Method": r.calculation_mode || salaryCalculationMode,
      "Salary Divisor": Number(r.salary_divisor) || 0,
      "Last Eligible Date": r.last_eligible_date || "",

      "Month Days": Number(r.month_days) || 0,
      "Working Days": Number(r.working_days) || 0,
      "Present Days": Number(r.present_days) || 0,
      "Half Days": Number(r.half_days) || 0,
      "Absent Days": Number(r.absent_days) || 0,
      "Leave Days": Number(r.leave_days) || 0,
      "Paid Leaves": Number(r.paid_leaves) || 0,
      "Unpaid Leave Days": Number(r.unpaid_leave_days) || 0,
      "Carry Days": Number(r.carry_days) || 0,

      "Overtime Hours": Number(r.overtime) || 0,

      "Base Salary": Number(r.base_salary) || 0,

      "Per Day Salary":
        Number(r.per_day_salary || 0),

      "Overtime Rate": Number(r.overtime_rate) || 0,
      "Earned Salary": Number(r.earned_salary) || 0,
      Bonus: Number(r.bonus) || 0,
      "Net Salary": Number(r.net_salary) || 0,

      "Payroll Hold": r.payroll_hold ? "Yes" : "No",
      Skippable: r.skippable ? "Yes" : "No",
      "Skip Reason": r.skip_reason || "",
    }));

    const fileName = `Salary_Preview_${monthNames[genMonth]}_${genYear}.xlsx`;

    downloadExcel(exportData, fileName, "Salary Preview");
  };

  const exportSalaryCalendarExcel = () => {
    if (!monthSalaryPreview) {
      SweetAlertShow(
        "info",
        "No Data",
        "There is no salary calendar data available to export.",
      );
      return;
    }

    const attendance = monthSalaryPreview.attendance || [];

    if (attendance.length === 0) {
      SweetAlertShow(
        "info",
        "No Data",
        "There is no attendance data available for this month.",
      );
      return;
    }

    const exportData = attendance.map((day, index) => ({
      "S.No.": index + 1,
      Date: day.date || "",
      Day: day.day || "",
      Status: day.status || "",
      "Attendance Type": day.attendance_type || "",

      "Check In": day.check_in || "",
      "Check Out": day.check_out || "",

      "Worked Hours": Number(day.worked_hours) || 0,
      "Required Hours": Number(day.required_hours) || 0,

      "Earned Salary": Number(day.salary_earned) || 0,
      "Overtime Hours": Number(day.overtime_hrs) || 0,
      "Overtime Bonus": Number(day.overtime_bonus) || 0,

      "Net Salary": Number(day.net_salary) || 0,
    }));

    const fileName = `Salary_Details_${calendarStaff?.staff_name || "Staff"}_${monthNames[calendarMonth]
      }_${calendarYear}.xlsx`;

    downloadExcel(exportData, fileName, "Date-wise Salary");
  };

  // ── Bulk pay generated salaries for the selected dashboard month ─────────
  const bulkPayableRows = staffRows.filter(
    (row) =>
      Boolean(month) &&
      row.is_generated &&
      row.salary_id &&
      Number(row.month_pending_amount ?? row.total_pending ?? 0) > 0,
  );

  const openBulkPay = () => {
    if (!month) {
      return SweetAlertShow(
        "info",
        "Select Month",
        "Please select one month first. Bulk salary payment is month-wise.",
      );
    }

    if (bulkPayableRows.length === 0) {
      return SweetAlertShow(
        "info",
        "Nothing Pending",
        `No generated pending salary found for ${monthNames[Number(month)]} ${year}.`,
      );
    }

    const ids = bulkPayableRows.map((row) => Number(row.salary_id));
    const amounts = {};
    bulkPayableRows.forEach((row) => {
      amounts[row.salary_id] = Number(
        row.month_pending_amount ?? row.total_pending ?? 0,
      ).toFixed(2);
    });

    setBulkSelectedIds(ids);
    setBulkAmounts(amounts);
    setBulkPayForm((prev) => ({
      ...prev,
      debit_account: prev.debit_account || bulkBanks?.[0]?.account_no || "",
      payment_date: prev.payment_date || new Date().toISOString().split("T")[0],
      remark: "",
    }));
    setBulkPayOpen(true);
  };

  const toggleBulkSalary = (salaryId) => {
    const id = Number(salaryId);
    setBulkSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAllBulkSalary = () => {
    const allIds = bulkPayableRows.map((row) => Number(row.salary_id));
    setBulkSelectedIds((prev) =>
      prev.length === allIds.length ? [] : allIds,
    );
  };

  const changeBulkAmount = (row, rawValue) => {
    const maxPending = Number(
      row.month_pending_amount ?? row.total_pending ?? 0,
    );
    if (rawValue === "") {
      setBulkAmounts((prev) => ({ ...prev, [row.salary_id]: "" }));
      return;
    }
    const value = Math.min(maxPending, Math.max(0, Number(rawValue || 0)));
    setBulkAmounts((prev) => ({
      ...prev,
      [row.salary_id]: value.toFixed(2),
    }));
  };

  const selectedBulkRows = bulkPayableRows.filter((row) =>
    bulkSelectedIds.includes(Number(row.salary_id)),
  );

  const bulkTotal = selectedBulkRows.reduce(
    (sum, row) => sum + Number(bulkAmounts[row.salary_id] || 0),
    0,
  );

  const submitBulkPay = async () => {
    if (!month) return;
    if (!bulkPayForm.debit_account) {
      return SweetAlertShow("info", "Bank Required", "Please select debit bank account.");
    }
    if (selectedBulkRows.length === 0) {
      return SweetAlertShow("info", "Select Staff", "Select at least one staff salary.");
    }

    const invalid = selectedBulkRows.find((row) => {
      const amount = Number(bulkAmounts[row.salary_id] || 0);
      const pending = Number(row.month_pending_amount ?? row.total_pending ?? 0);
      return amount <= 0 || amount > pending + 0.001;
    });
    if (invalid) {
      return SweetAlertShow(
        "info",
        "Invalid Amount",
        `Check payment amount for ${invalid.name}. Amount cannot exceed pending salary.`,
      );
    }

    const confirm = await Swal.fire({
      title: "Confirm Bulk Salary Payment",
      html: `Pay <b>${rupee(bulkTotal)}</b> to <b>${selectedBulkRows.length} staff</b> for <b>${monthNames[Number(month)]} ${year}</b>?<br/><small class="text-muted">Each selected staff will get a normal salary payment entry. Unselected staff will be skipped.</small>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Pay Selected",
      cancelButtonText: "Cancel",
    });
    if (!confirm?.isConfirmed) return;

    setBulkPayLoading(true);
    try {
      const res = await staffSalaryBulkPay({
        month: Number(month),
        year: Number(year),
        debit_account: bulkPayForm.debit_account,
        payment_date: bulkPayForm.payment_date,
        remark: bulkPayForm.remark || "",
        payments: selectedBulkRows.map((row) => ({
          salary_id: Number(row.salary_id),
          staff_id: Number(row.staff_id),
          amount: Number(bulkAmounts[row.salary_id] || 0),
        })),
      });

      if (!res?.data?.status) {
        throw new Error(res?.data?.message || "Bulk salary payment failed.");
      }

      ToastMain.fire({ icon: "success", title: res.data.message });
      setBulkPayOpen(false);
      setBulkSelectedIds([]);
      setBulkAmounts({});
      await fetchSummary();
      if (selectedStaffId) await fetchDetail(selectedStaffId, year);
    } catch (err) {
      SweetAlertShow(
        "error",
        "Payment Failed",
        err?.response?.data?.message || err?.message || "Bulk salary payment failed.",
      );
    } finally {
      setBulkPayLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row">
                  <StaffLayout />

                  {/* Page title */}
                  <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                    <div>
                      <h3 className="fw-bold text-primary mb-1">
                        <i className="ri-bar-chart-box-line me-2"></i>Staff
                        Salary Report
                      </h3>
                      <p className="text-muted mb-0">
                        Month-wise attendance, earnings and payment status for
                        every staff member.
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {selectedStaffId && (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleBackToDashboard}
                        >
                          <i className="ri-arrow-left-line me-1"></i>Back to
                          Dashboard
                        </button>
                      )}

                      {hasAccess("/staff/salary/generate") && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={openGenerateModal}
                        >
                          <i className="ri-magic-line me-1"></i>
                          Generate Salary
                        </button>
                      )}
                      <YearSelect />
                    </div>
                  </div>

                  {/* ══════════════ TIER 1 — DASHBOARD ══════════════ */}
                  {!selectedStaffId && (
                    <>
                      {/* KPI cards */}
                      {summary && (
                        <div className="row g-3 mb-4">
                          <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body">
                                <div className="text-muted small mb-1">
                                  Total Staff
                                </div>
                                <h4 className="fw-bold mb-0">
                                  {summary.total_staff}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body">
                                <div className="text-muted small mb-1">
                                  Staff With Pending Salary
                                </div>
                                <h4 className="fw-bold text-danger mb-0">
                                  {summary.pending_staff_count}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body">
                                <div className="text-muted small mb-1">
                                  Total Pending Amount
                                </div>
                                <h4 className="fw-bold text-danger mb-0">
                                  {rupee(summary.total_pending)}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body">
                                <div className="text-muted small mb-1">
                                  Total Paid ({year})
                                </div>
                                <h4 className="fw-bold text-success mb-0">
                                  {rupee(summary.total_paid)}
                                </h4>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Staff list */}
                      <div className="card border-0 shadow-sm">
                        {/* <div className="card-header bg-label-primary d-flex align-items-center justify-content-between flex-wrap gap-2 border-0"> */}
                        <div className="card-header bg-label-primary d-flex align-items-center justify-content-between flex-wrap gap-2 border-0">
                          {/* <span className="fw-semibold">
                            <i className="ri-team-line me-2"></i>All Staff —
                            {year}
                          </span> */}
                          <div className="fw-semibold">
                            <i className="ri-team-line me-2"></i>
                            Staff Salary Report —{" "}
                            {month
                              ? monthNames[Number(month)]
                              : "All Months"}{" "}
                            {year}
                          </div>

                          <div className="d-flex gap-2 flex-wrap">
                            <div className="d-flex gap-2">

                              {hasAccess("/staff/salary/pay") && (

                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={openBulkPay}
                                  disabled={!month || bulkPayableRows.length === 0}
                                  title={!month ? "Select a month first" : "Pay selected generated salaries"}
                                >
                                  <i className="ri-money-rupee-circle-line me-1"></i>
                                  Bulk Mark Paid
                                </button>
                              )}


                              {hasAccess("/staff/print") && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-dark"
                                  onClick={() =>
                                    printTable(
                                      "staff-report-table",
                                      `Staff Salary Report - ${year}`,
                                    )
                                  }
                                >
                                  <i className="ri-printer-line me-1"></i>
                                  Print
                                </button>
                              )}
                              {hasAccess("/staff/excel") && (
                                <button
                                  type="button"
                                  className="btn btn-outline-success salary-excel-btn"
                                  onClick={exportStaffReportExcel}
                                >
                                  <i className="ri-file-excel-2-line me-1"></i>
                                  Excel
                                </button>
                              )}
                            </div>
                            <div className=" form-floating-outline mb-0">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search Name / Mobile / Reg."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ minWidth: 220 }}
                              />
                            </div>

                            <div className=" form-floating-outline mb-0">
                              <select
                                className="form-select form-select-sm"
                                value={staffType}
                                onChange={(e) => setStaffType(e.target.value)}
                                style={{ minWidth: 180 }}
                              >
                                <option value="">All Types</option>
                                <option value="Teaching">Teaching</option>
                                <option value="Administrative">
                                  Administrative
                                </option>
                                <option value="Other">Other</option>
                              </select>
                            </div>

                            <div className=" form-floating-outline mb-0">
                              <select
                                className="form-select form-select-sm"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                style={{ minWidth: 150 }}
                              >
                                <option value="">All Months</option>
                                <option value="1">Jan</option>
                                <option value="2">Feb</option>
                                <option value="3">Mar</option>
                                <option value="4">Apr</option>
                                <option value="5">May</option>
                                <option value="6">Jun</option>
                                <option value="7">Jul</option>
                                <option value="8">Aug</option>
                                <option value="9">Sep</option>
                                <option value="10">Oct</option>
                                <option value="11">Nov</option>
                                <option value="12">Dec</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table
                            className="table align-middle table-hover mb-0"
                            id="staff-report-table"
                          >
                            <thead className="table-light">
                              <tr>
                                <th>Staff</th>
                                <th>Type</th>
                                <th className="text-end">Base Salary</th>
                                <th className="text-end">Total Earned</th>
                                <th className="text-end">Total Paid</th>
                                <th className="text-end">Pending</th>
                                <th className="text-center">Pending Months</th>
                                <th className="text-center">Status</th>
                                <th className="text-center" style={{ minWidth: 150 }}>Actions</th>
                                <th className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {staffRows.length === 0 && (
                                <tr>
                                  <td
                                    colSpan={9}
                                    className="text-center text-muted py-4"
                                  >
                                    <i className="ri-user-search-line fs-1 d-block mb-1"></i>
                                    No staff found for {year}
                                  </td>
                                </tr>
                              )}
                              {staffRows.map((s) => (
                                <tr
                                  key={s.staff_id}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleSelectStaff(s)}
                                >
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      {s.img ? (
                                        <img
                                          src={`${imgBaseUrl}${s.img}`}
                                          alt={s.name}
                                          className="rounded-circle border"
                                          style={{
                                            width: 36,
                                            height: 36,
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center fw-bold text-primary"
                                          style={{
                                            width: 36,
                                            height: 36,
                                            fontSize: 12,
                                          }}
                                        >
                                          {initials(s.name)}
                                        </div>
                                      )}
                                      <div>
                                        <Link
                                          to={`/teachers/view/${s.staff_id}`}
                                          className="fw-semibold small text-decoration-none"
                                          onClick={(e) => e.stopPropagation()}
                                          title="View Staff Profile"
                                        >
                                          {s.name}
                                        </Link>

                                        <div
                                          className="text-muted"
                                          style={{ fontSize: 11 }}
                                        >
                                          Reg: {s.registration_no}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>{s.staff_type}</td>
                                  <td className="text-end">
                                    {rupee(s.base_salary)}
                                  </td>
                                  <td className="text-end">
                                    {rupee(s.total_earned)}
                                  </td>
                                  <td className="text-end text-success">
                                    {rupee(s.total_paid)}
                                  </td>
                                  <td className="text-end fw-bold text-danger">
                                    {rupee(s.total_pending)}
                                  </td>
                                  <td className="text-center">
                                    {s.pending_months > 0 ? (
                                      <span className="badge bg-label-danger rounded-pill">
                                        {s.pending_months}
                                      </span>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {statusBadge(s.status)}
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex justify-content-center gap-1">
                                      {hasAccess("/staff/dashboard/view/report") && (

                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectStaff(s);
                                          }}
                                        >
                                          <i className="ri-eye-line me-1"></i>
                                          View Report
                                        </button>
                                      )}

                                      {hasAccess("/staff/dashboard/salary/details") && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-success"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openSalaryCalendar(
                                              s,
                                              Number(month) || null,
                                              Number(year),
                                              {
                                                source: "dashboard",
                                                salarySnapshot:
                                                  month && s.is_generated
                                                    ? buildCalendarSalarySnapshot(
                                                      s,
                                                      Number(month),
                                                      Number(year),
                                                      "dashboard",
                                                    )
                                                    : null,
                                              },
                                            );
                                          }}
                                        >
                                          <i className="ri-calendar-line me-1"></i>
                                          Salary Details
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ══════════════ TIER 2 — STAFF DETAIL ══════════════ */}
                  {selectedStaffId && detail?.data && (
                    <>
                      {/* Staff + summary strip */}
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            {detail.data.img ? (
                              <img
                                src={`${imgBaseUrl}${detail.data.img}`}
                                alt={detail.data.name}
                                className="rounded-circle border"
                                style={{
                                  width: 48,
                                  height: 48,
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center fw-bold text-primary"
                                style={{
                                  width: 48,
                                  height: 48,
                                  fontSize: 16,
                                }}
                              >
                                {initials(detail.data.name)}
                              </div>
                            )}
                            <div>
                              <h5 className="mb-0 fw-bold">
                                <Link
                                  to={`/teachers/view/${detail.data.id}`}
                                  className="text-decoration-none"
                                  onClick={(e) => e.stopPropagation()}
                                  title="View Staff Profile"
                                >
                                  {detail.data.name}
                                </Link>
                              </h5>
                              <small className="text-muted">
                                {detail.data.staff_type} · Reg:
                                {detail.data.registration_no} ·
                                <i className="ri-phone-line me-1"></i>
                                {detail.data.mobile}
                              </small>
                            </div>
                            {detail.summary && (
                              <div className="ms-auto d-flex gap-2 flex-wrap">
                                <div className="text-center border rounded-3 px-3 py-2">
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    Pending Months
                                  </div>
                                  <div className="fw-bold text-danger">
                                    {detail.summary.pending_months}
                                  </div>
                                </div>
                                <div className="text-center border rounded-3 px-3 py-2">
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    Total Pending
                                  </div>
                                  <div className="fw-bold text-danger">
                                    {rupee(detail.summary.total_pending)}
                                  </div>
                                </div>
                                <div className="text-center border rounded-3 px-3 py-2">
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    Total Paid
                                  </div>
                                  <div className="fw-bold text-success">
                                    {rupee(detail.summary.total_paid)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Advance banner */}
                      {detail.current_advance && (
                        <div className="alert alert-info d-flex align-items-center gap-2 mb-3">
                          <i className="ri-information-line fs-5"></i>
                          <div>
                            <strong>Advance Given:</strong>
                            {rupee(detail.current_advance.advance_given)} has
                            already been paid as advance for
                            <strong>
                              {detail.current_advance.month_label}
                            </strong>
                            . It will be deducted once that month's salary is
                            generated.
                          </div>
                        </div>
                      )}

                      {/* Monthly report table — GENERATED months only */}
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-header border-0 bg-transparent d-flex align-items-center justify-content-between">
                          <h6 className="fw-bold mb-0">
                            <i className="ri-calendar-2-line me-2 text-primary"></i>
                            Monthly Report — {year}
                          </h6>
                          {payableMonths.length > 0 && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={toggleAll}
                            >
                              {checkedIds.length === payableMonths.length
                                ? "Deselect All"
                                : "Select All Pending"}
                            </button>
                          )}
                        </div>
                        <div className="table-responsive">
                          <table className="table align-middle table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th width="42"></th>
                                <th>Month</th>
                                <th>Attendance</th>
                                <th className="text-end">Base</th>
                                <th className="text-end">Net Salary</th>
                                <th className="text-end">Paid</th>
                                <th className="text-end text-danger">Pending</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {generatedMonths.length === 0 && (
                                <tr>
                                  <td colSpan={9} className="text-center text-muted py-4">
                                    No salary generated yet for {year}.
                                  </td>
                                </tr>
                              )}

                              {generatedMonths.map((m) => {
                                const isChecked = m.salary_id && checkedIds.includes(m.salary_id);
                                const canCheck = m.payable && Number(m.pending_amount || 0) > 0 && m.salary_id;
                                const canEdit = canEditGeneratedSalary(m);
                                const canRegenerate = Number(m.paid_amount || 0) <= 0;

                                return (
                                  <tr
                                    key={`${m.year}-${m.month}`}
                                    className={isChecked ? "table-primary" : ""}
                                  >
                                    <td onClick={(e) => e.stopPropagation()}>
                                      {canCheck ? (
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={isChecked}
                                          onChange={() => toggleCheck(m.salary_id)}
                                        />
                                      ) : null}
                                    </td>

                                    <td>
                                      <div className="fw-semibold">{m.month_label}</div>
                                      <small className="text-muted">
                                        {Number(m.total_working_days || 0)} working days
                                      </small>
                                    </td>

                                    <td>
                                      <div className="small d-flex flex-wrap gap-2">
                                        <span className="text-success">P {Number(m.present_days || 0)}</span>
                                        {Number(m.half_days || 0) > 0 && (
                                          <span className="text-warning">H {Number(m.half_days || 0)}</span>
                                        )}
                                        {Number(m.absent_days || 0) > 0 && (
                                          <span className="text-danger">A {Number(m.absent_days || 0)}</span>
                                        )}
                                        {Number(m.leave_days || 0) > 0 && (
                                          <span className="text-info">L {Number(m.leave_days || 0)}</span>
                                        )}
                                        {Number(m.overtime_hours || 0) > 0 && (
                                          <span className="text-primary">OT {Number(m.overtime_hours || 0)}h</span>
                                        )}
                                      </div>
                                    </td>

                                    <td className="text-end">{rupee(m.base_salary)}</td>
                                    <td className="text-end fw-bold">{rupee(m.net_salary)}</td>
                                    <td className="text-end text-success">{rupee(m.paid_amount)}</td>
                                    <td className="text-end fw-bold text-danger">{rupee(m.pending_amount)}</td>
                                    <td className="text-center">{statusBadge(m.payment_status)}</td>

                                    <td className="text-center">
                                      <div className="d-flex justify-content-center gap-1">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-success"
                                          title={`View ${m.month_label} attendance and salary details`}
                                          onClick={() =>
                                            openSalaryCalendar(
                                              {
                                                ...m,
                                                staff_id: detail.data.id,
                                                staff_name: detail.data.name,
                                                staff_img: detail.data.img,
                                              },
                                              Number(m.month),
                                              Number(m.year),
                                              {
                                                source: "generated_salary",
                                                salarySnapshot:
                                                  buildCalendarSalarySnapshot(
                                                    m,
                                                    Number(m.month),
                                                    Number(m.year),
                                                    "generated_salary",
                                                  ),
                                              },
                                            )
                                          }
                                        >
                                          <i className="ri-eye-line"></i>
                                        </button>

                                        {canEdit && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            title={
                                              Number(m.paid_amount || 0) > 0
                                                ? "Edit latest paid salary"
                                                : "Edit generated salary"
                                            }
                                            onClick={() => openGeneratedSalaryEdit(m)}
                                          >
                                            <i className="ri-edit-2-line"></i>
                                          </button>
                                        )}

                                        {canRegenerate && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-warning"
                                            title="Recalculate complete salary"
                                            onClick={() => regenerateGeneratedSalary(m)}
                                          >
                                            <i className="ri-refresh-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>

                            {checkedIds.length > 0 && (
                              <tfoot className="table-light fw-bold">
                                <tr>
                                  <td colSpan={6} className="text-end">
                                    Selected ({checkedIds.length} month{checkedIds.length > 1 ? "s" : ""}) — Total Pending:
                                  </td>
                                  <td className="text-end text-danger fs-6">
                                    {rupee(totalSelectedPending)}
                                  </td>
                                  <td colSpan={2}></td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>

                        {/* Collapsible strip for months not yet generated */}
                        {notGeneratedMonths.length > 0 && (
                          <div className="border-top">
                            <button
                              type="button"
                              className="btn btn-link btn-sm w-100 text-decoration-none d-flex align-items-center justify-content-between py-2 px-3"
                              onClick={() =>
                                setShowNotGenerated((prev) => !prev)
                              }
                            >
                              <span className="text-muted">
                                <i className="ri-error-warning-line me-1 text-warning"></i>
                                {notGeneratedMonths.length} month
                                {notGeneratedMonths.length > 1 ? "s" : ""} not
                                yet generated
                              </span>
                              <i
                                className={`ri-arrow-${showNotGenerated ? "up" : "down"}-s-line`}
                              ></i>
                            </button>

                            {showNotGenerated && (
                              <div className="table-responsive border-top">
                                <table className="table table-sm align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Month</th>
                                      <th className="text-center">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {notGeneratedMonths.map((m) => (
                                      <tr key={`ng-${m.year}-${m.month}`}>
                                        <td className="text-muted">
                                          {m.month_label}
                                        </td>
                                        <td className="text-center">
                                          <span
                                            className="badge bg-label-secondary"
                                            style={{ fontSize: 10 }}
                                          >
                                            Not Generated
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div className="alert alert-warning m-3 mb-3 py-2 small d-flex align-items-center justify-content-between flex-wrap gap-2">
                                  <span>
                                    <i className="ri-error-warning-line me-1"></i>
                                    Generate salary for these months using the
                                    button above to enable payment against them
                                    here.
                                  </span>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={openGenerateModal}
                                  >
                                    <i className="ri-magic-line me-1"></i>
                                    Generate Salary
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Payment form — appears when months are selected */}
                      {checkedIds.length > 0 && (
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-body">
                            <form onSubmit={handlePay}>
                              <div className="border rounded-3 bg-light p-3 mb-3">
                                <div className="fw-semibold mb-1">
                                  <i className="ri-checkbox-multiple-line me-2 text-primary"></i>
                                  Paying for:
                                  {selectedRows
                                    .map((s) => s.month_label)
                                    .join(", ")}
                                </div>
                                <small className="text-muted">
                                  Total pending:
                                  <strong className="text-danger">
                                    {rupee(totalSelectedPending)}
                                  </strong>
                                </small>
                              </div>

                              <div className="row g-3">
                                <div className="col-md-6">
                                  <BankSelect />
                                </div>
                                <div className="col-md-6">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="date"
                                      className="form-control"
                                      value={payForm.payment_date}
                                      onChange={(e) =>
                                        setPayForm((p) => ({
                                          ...p,
                                          payment_date: e.target.value,
                                        }))
                                      }
                                    />
                                    <label>Payment Date</label>
                                  </div>
                                </div>

                                <div className="col-12">
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="customToggle"
                                      checked={useCustomAmount}
                                      onChange={(e) => {
                                        setUseCustomAmount(e.target.checked);
                                        if (!e.target.checked)
                                          setCustomAmount("");
                                      }}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="customToggle"
                                    >
                                      Pay a partial / custom amount
                                    </label>
                                  </div>
                                </div>

                                <div className="col-md-6">
                                  {useCustomAmount ? (
                                    <div className="form-floating form-floating-outline">
                                      <input
                                        type="number"
                                        className="form-control"
                                        min="1"
                                        max={totalSelectedPending}
                                        value={customAmount}
                                        onChange={(e) =>
                                          setCustomAmount(e.target.value)
                                        }
                                        placeholder="Amount"
                                        required
                                      />
                                      <label>
                                        Custom Amount (max:
                                        {rupee(totalSelectedPending)})
                                      </label>
                                    </div>
                                  ) : (
                                    <div className="border rounded-3 p-3 bg-white">
                                      <small className="text-muted d-block mb-1">
                                        Amount to Pay
                                      </small>
                                      <h5 className="fw-bold text-success mb-0">
                                        {rupee(totalSelectedPending)}
                                      </h5>
                                    </div>
                                  )}
                                </div>

                                <div className="col-md-6">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Remark"
                                      value={payForm.remark}
                                      onChange={(e) =>
                                        setPayForm((p) => ({
                                          ...p,
                                          remark: e.target.value,
                                        }))
                                      }
                                    />
                                    <label>Remark (optional)</label>
                                  </div>
                                </div>

                                <div className="col-12">
                                  <div className="d-flex align-items-center justify-content-between bg-label-success rounded-3 p-3">
                                    <div>
                                      <small className="text-muted d-block">
                                        Final Amount
                                      </small>
                                      <h5 className="fw-bold text-success mb-0">
                                        {rupee(normalPayAmount)}
                                      </h5>
                                    </div>
                                    <button
                                      type="submit"
                                      className="btn btn-success px-5"
                                      disabled={loading || normalPayAmount <= 0}
                                    >
                                      <i className="ri-check-double-line me-2"></i>
                                      {loading ? "Processing..." : "Pay Now"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      )}

                      {/* Advance mini-form when current month isn't generated yet */}
                      {!detail.current_month_generated && (
                        <div className="card border-0 shadow-sm">
                          <div className="card-header bg-label-warning border-0 fw-semibold">
                            <i className="ri-send-plane-line me-2"></i>Pay
                            Advance for Current Month
                          </div>
                          <div className="card-body">
                            <form onSubmit={handleAdvancePay}>
                              <div className="row g-3">
                                <div className="col-md-4">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="number"
                                      className="form-control"
                                      placeholder="0"
                                      min="1"
                                      value={advanceAmount}
                                      onChange={(e) =>
                                        setAdvanceAmount(e.target.value)
                                      }
                                      required
                                    />
                                    <label>Advance Amount (₹)</label>
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <BankSelect />
                                </div>
                                <div className="col-md-4">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="date"
                                      className="form-control"
                                      value={payForm.payment_date}
                                      onChange={(e) =>
                                        setPayForm((p) => ({
                                          ...p,
                                          payment_date: e.target.value,
                                        }))
                                      }
                                    />
                                    <label>Payment Date</label>
                                  </div>
                                </div>
                                <div className="col-12 text-end">
                                  <button
                                    type="submit"
                                    className="btn btn-warning px-4"
                                    disabled={loading}
                                  >
                                    <i className="ri-send-plane-line me-2"></i>
                                    {loading ? "Processing..." : "Pay Advance"}
                                  </button>
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════ GENERATE SALARY MODAL ══════════════════ */}
      {/* Edit Generated Salary Modal */}
      {editSalary && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.45)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">Edit Generated Salary</h5>
                  <small className="text-muted">{editSalary.month_label} · paid amount remains locked</small>
                </div>
                <button type="button" className="btn-close" disabled={editSalaryLoading} onClick={() => setEditSalary(null)} />
              </div>
              <div className="modal-body">
                <div className="alert alert-info py-2 small">
                  Base salary and already-paid amount are audit values. Edit earned salary/attendance summary only when correcting a genuine payroll mistake.
                </div>
                <div className="row g-3">
                  <div className="col-md-4"><label className="form-label">Base Salary</label><input className="form-control" value={editSalary.base_salary} disabled /></div>
                  <div className="col-md-4"><label className="form-label">Earned Salary</label><input type="number" min="0" max={editSalary.base_salary} step="0.01" className="form-control" value={editSalary.earned_salary} onChange={(e) => changeGeneratedSalaryEdit("earned_salary", e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label">Deduction</label><input className="form-control" value={editSalary.deduction} disabled /></div>
                  <div className="col-md-4"><label className="form-label">Bonus / OT</label><input type="number" min="0" step="0.01" className="form-control" value={editSalary.bonus} onChange={(e) => changeGeneratedSalaryEdit("bonus", e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label">Net Salary</label><input className="form-control fw-bold" value={editSalary.net_salary} disabled /></div>
                  <div className="col-md-4"><label className="form-label">Already Paid</label><input className="form-control" value={editSalary.paid_amount} disabled /></div>
                  <div className="col-md-3"><label className="form-label">Working Days</label><input type="number" min="0" className="form-control" value={editSalary.working_days} onChange={(e) => changeGeneratedSalaryEdit("working_days", e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label">Present</label><input type="number" min="0" className="form-control" value={editSalary.present_days} onChange={(e) => changeGeneratedSalaryEdit("present_days", e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label">Half Days</label><input type="number" min="0" className="form-control" value={editSalary.half_days} onChange={(e) => changeGeneratedSalaryEdit("half_days", e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label">Leave</label><input type="number" min="0" className="form-control" value={editSalary.leave_days} onChange={(e) => changeGeneratedSalaryEdit("leave_days", e.target.value)} /></div>
                  <div className="col-12"><label className="form-label">Correction Remark</label><textarea className="form-control" rows="2" value={editSalary.remark} onChange={(e) => changeGeneratedSalaryEdit("remark", e.target.value)} placeholder="Why was this generated salary corrected?" /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" disabled={editSalaryLoading} onClick={() => setEditSalary(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={editSalaryLoading} onClick={saveGeneratedSalaryEdit}>
                  {editSalaryLoading ? "Updating..." : "Update Salary"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGenerateModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="modal-dialog"
            style={{
              width: "100vw",
              maxWidth: "100vw",
              height: "100vh",
              margin: 0,
            }}
          >
            <div
              className="modal-content"
              style={{ height: "100vh", borderRadius: 0, border: 0 }}
            >
              <div className="modal-header border-bottom bg-white" style={{ flex: "0 0 auto" }}>
                <h5 className="modal-title fw-bold">
                  <i className="ri-magic-line me-2 text-primary"></i>
                  Generate Monthly Salary
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeGenerateModal}
                  disabled={genLoading}
                ></button>
              </div>

              <div className="modal-body" style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
                {/* ── STEP 1: pick month/year ─────────────────────────── */}
                {genStep === "pick" && (
                  <>
                    <p className="text-muted small">
                      Select the month to generate salary for. This will
                      calculate attendance-based earnings for every eligible
                      staff member — nothing is saved until you review and
                      confirm on the next step.
                    </p>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={genMonth}
                            onChange={(e) =>
                              setGenMonth(parseInt(e.target.value, 10))
                            }
                          >
                            {monthNames.slice(1).map((m, idx) => (
                              <option key={idx + 1} value={idx + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                          <label>Month</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={genYear}
                            onChange={(e) =>
                              setGenYear(parseInt(e.target.value, 10))
                            }
                          >
                            {yearOptions.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                          <label>Year</label>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="form-label fw-semibold">Salary Calculation Method</label>
                      <select
                        className="form-select"
                        value={salaryCalculationMode}
                        onChange={(e) => setSalaryCalculationMode(e.target.value)}
                      >
                        <option value="calendar_days">Month Days</option>
                        <option value="working_days">Working Days</option>
                      </select>
                      <div className="form-text">
                        {salaryCalculationMode === "calendar_days" &&
                          "Per-day salary = monthly salary ÷ actual month days (28/29/30/31). Half day = 50%; low-time/hour deduction uses the same per-day rate."}
                        {salaryCalculationMode === "working_days" &&
                          "Per-day salary = monthly salary ÷ scheduled working days for that employee. Half day = 50%; low-time/hour deduction uses the same per-day rate."}
                      </div>
                    </div>
                  </>
                )}

                {/* ── STEP 2: preview & edit ──────────────────────────── */}
                {genStep === "preview" && (
                  <>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="fw-semibold">
                          {monthNames[genMonth]} {genYear}
                        </div>
                        <small className="text-muted">
                          Review and edit before saving. Nothing is written to
                          the database yet. Change Present Days to recalculate salary,
                          or directly adjust the final Deduction amount.
                        </small>
                        <div className="alert alert-light border py-2 px-3 mb-3 small">
                          <i className="ri-information-line me-1 text-primary"></i>
                          <strong>Salary calculation:</strong> {salaryCalculationMode === "calendar_days" ? "Month Days — monthly salary ÷ actual calendar days." : "Working Days — monthly salary ÷ scheduled working days."} Absence = 1 day, Half Day = 0.5 day, and low-time/hour shortage uses the same selected per-day rate. Overtime payment is currently disabled.
                        </div>
                        <div className={`border rounded-3 p-3 mb-3 ${payIncompleteAttendance ? "bg-label-success" : "bg-label-warning"}`}>
                          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                            <div>
                              <div className="fw-semibold">
                                <i className="ri-fingerprint-line me-1"></i>
                                Pay True Incomplete Attendance Days
                              </div>
                              <small className="text-muted">
                                {payIncompleteAttendance
                                  ? "ON — a day with punch-in/one attendance mark but missing checkout receives full per-day salary."
                                  : "OFF — incomplete/missing-checkout days are deducted from salary."}
                              </small>
                            </div>
                            <button
                              type="button"
                              className={`btn ${payIncompleteAttendance ? "btn-success" : "btn-outline-warning"}`}
                              onClick={handleIncompleteSalaryToggle}
                              disabled={genLoading}
                            >
                              <i className={`${payIncompleteAttendance ? "ri-toggle-fill" : "ri-toggle-line"} me-1`}></i>
                              {genLoading ? "Recalculating..." : payIncompleteAttendance ? "ON — Salary Applied" : "OFF — Salary Deducted"}
                            </button>
                          </div>
                        </div>
                      </div>
                      {previewSummary && (
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                          <span className="badge bg-label-primary">
                            {previewSummary.payable_count} payable
                          </span>

                          <span className="badge bg-label-secondary">
                            {previewSummary.skipped_count} skipped
                          </span>

                          <span className="badge bg-label-success">
                            Total: {rupee(previewTotalNet)}
                          </span>

                          <button
                            type="button"
                            className="btn btn-outline-success salary-excel-btn"
                            onClick={exportSalaryPreviewExcel}
                            disabled={!previewRows.length}
                          >
                            <i className="ri-file-excel-2-line me-1"></i>
                            Export Excel
                          </button>
                        </div>
                      )}
                    </div>

                    {payablePreviewRows.length === 0 && (
                      <div className="alert alert-info">
                        No payable staff for this month — everyone is either
                        already generated or missing an employment record.
                      </div>
                    )}

                    <div className="row g-2 mb-3">
                      {[
                        ["Monthly Base", payrollTotals.base, "ri-wallet-3-line", "text-primary"],
                        ["Generated Salary", payrollTotals.net, "ri-money-rupee-circle-line", "text-success"],
                        ["Total Deduction", payrollTotals.deduction, "ri-subtract-line", "text-danger"],
                        ["Leave Deduction", payrollTotals.leave, "ri-calendar-close-line", "text-danger"],
                        ["Absent Deduction", payrollTotals.absence, "ri-user-unfollow-line", "text-danger"],
                        ["Low Time / Half Day", payrollTotals.lowTime, "ri-time-line", "text-warning"],
                        ["Incomplete Attendance", payrollTotals.incomplete, "ri-error-warning-line", "text-warning"],
                        ["Payable Staff", previewSummary?.payable_count || payablePreviewRows.length, "ri-team-line", "text-info", true],
                      ].map(([label, value, icon, cls, raw]) => (
                        <div className="col-xl-3 col-md-4 col-6" key={label}>
                          <div className="border rounded-3 p-3 h-100 bg-light">
                            <div className="d-flex justify-content-between gap-2">
                              <small className="text-muted">{label}</small>
                              <i className={`${icon} ${cls}`}></i>
                            </div>
                            <div className={`fw-bold mt-1 ${cls}`}>{raw ? value : rupee(value)}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {payablePreviewRows.length > 0 && (
                      <>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 p-2 border rounded-3 bg-light">
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-semibold small">
                              {selectedPayablePreviewRows.length} of {payablePreviewRows.length} staff selected
                            </span>
                            <span className="badge bg-label-success">
                              Selected: {rupee(selectedPayablePreviewRows.reduce((sum, r) => sum + Number(r.net_salary || 0), 0))}
                            </span>
                          </div>
                          <div className="d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllPreviewStaff} disabled={allPayableSelected}>
                              <i className="ri-checkbox-multiple-line me-1"></i>Select All
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearPreviewStaff} disabled={!selectedPreviewStaffIds.length}>
                              Clear All
                            </button>
                          </div>
                        </div>

                        <div className="border rounded-3 overflow-hidden">
                          <table className="table table-sm align-middle mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
                            <thead className="table-light">
                              <tr>
                                <th className="text-center" style={{ width: 42 }}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={allPayableSelected}
                                    onChange={(e) => e.target.checked ? selectAllPreviewStaff() : clearPreviewStaff()}
                                    aria-label="Select all staff"
                                  />
                                </th>
                                <th style={{ width: "24%" }}>Staff</th>
                                <th style={{ width: "27%" }}>Attendance</th>
                                <th className="text-end" style={{ width: "14%" }}>Base Salary</th>
                                <th className="text-end" style={{ width: "16%" }}>Deduction</th>
                                <th className="text-end" style={{ width: "13%" }}>Net Salary</th>
                                <th className="text-center" style={{ width: 78 }}>View</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payablePreviewRows.map((r) => {
                                const selected = selectedPreviewStaffIds.includes(r.staff_id);
                                return (
                                  <tr key={r.staff_id} className={selected ? "" : "opacity-50"}>
                                    <td className="text-center">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selected}
                                        onChange={() => togglePreviewStaff(r.staff_id)}
                                        aria-label={`Select ${r.staff_name}`}
                                      />
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        {r.staff_img ? (
                                          <img src={`${imgBaseUrl}${r.staff_img}`} alt={r.staff_name} className="rounded-circle border flex-shrink-0" style={{ width: 30, height: 30, objectFit: "cover" }} />
                                        ) : (
                                          <div className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center fw-bold text-primary flex-shrink-0" style={{ width: 30, height: 30, fontSize: 10 }}>
                                            {initials(r.staff_name)}
                                          </div>
                                        )}
                                        <div className="text-truncate">
                                          <div className="fw-semibold text-truncate" style={{ fontSize: 12 }}>{r.staff_name}</div>
                                          <small className="text-muted">{r.registration_no || "—"}</small>
                                          {r.__manual_adjusted && <span className="badge bg-label-warning ms-1" style={{ fontSize: 9 }}>Adjusted</span>}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <label className="small text-muted mb-0">Present</label>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm text-center"
                                          style={{ width: 62 }}
                                          value={r.present_days}
                                          min="0"
                                          onChange={(e) => updatePreviewRow(r.staff_id, "present_days", e.target.value)}
                                        />
                                        <small className="text-muted">
                                          / {salaryCalculationMode === "working_days" ? (r.working_days || 0) : (r.month_days || 0)}
                                        </small>
                                      </div>
                                      <div className="d-flex align-items-center flex-wrap gap-1 mt-1" style={{ fontSize: 11 }}>
                                        <span className="text-muted">Half {r.half_days || 0}</span>
                                        <span className="text-muted">· Absent {r.absent_days || 0}</span>
                                        {(Number(r.paid_leaves) || 0) > 0 && (
                                          <span className="badge bg-label-success" style={{ fontSize: 10 }}>
                                            Paid Leave {Number(r.paid_leaves) || 0} · ₹0 Deduction
                                          </span>
                                        )}
                                        <span className="text-muted">· Unpaid Leave {r.unpaid_leave_days || 0}</span>
                                        {(() => {
                                          const incompleteCount = getIncompleteAttendanceCount(r);
                                          const incompleteDeduction = Number(
                                            r?.deduction_breakdown?.incomplete_attendance || 0,
                                          );
                                          const hasIncomplete =
                                            (incompleteCount !== null && incompleteCount > 0) ||
                                            incompleteDeduction > 0;

                                          return hasIncomplete ? (
                                            <span
                                              className="badge bg-label-warning border border-warning"
                                              style={{ fontSize: 10 }}
                                              title={
                                                incompleteCount !== null
                                                  ? `${incompleteCount} incomplete attendance day(s)`
                                                  : "Incomplete attendance detected"
                                              }
                                            >
                                              <i className="ri-error-warning-line me-1"></i>
                                              Incomplete {incompleteCount !== null ? incompleteCount : "!"}
                                            </span>
                                          ) : (
                                            <span className="text-muted">· Incomplete 0</span>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                    <td className="text-end">
                                      <div className="fw-semibold">{rupee(r.base_salary)}</div>
                                      <small className="text-muted">
                                        {rupee(r.per_day_salary)} / day
                                      </small>
                                    </td>
                                    <td>
                                      <div className="input-group input-group-sm ms-auto" style={{ maxWidth: 125 }}>
                                        <span className="input-group-text">₹</span>
                                        <input
                                          type="number"
                                          className={`form-control text-end ${r.__manual_adjusted ? "border-warning" : ""}`}
                                          value={r.deduction}
                                          min="0"
                                          max={Number(r.base_salary || 0)}
                                          step="0.01"
                                          onChange={(e) => updatePreviewRow(r.staff_id, "deduction", e.target.value)}
                                        />
                                      </div>
                                    </td>
                                    <td className="text-end fw-bold text-success">{rupee(r.net_salary)}</td>
                                    <td className="text-center">
                                      <div className="d-flex justify-content-center gap-1">
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() =>
                                          openSalaryCalendar(r, genMonth, genYear, {
                                            source: "generation_preview",
                                            salarySnapshot:
                                              buildCalendarSalarySnapshot(
                                                r,
                                                genMonth,
                                                genYear,
                                                "generation_preview",
                                              ),
                                          })
                                        } disabled={monthSalaryLoading} title="Attendance calendar">
                                          <i className="ri-calendar-line"></i>
                                        </button>
                                        {r.__manual_adjusted && (
                                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => resetPreviewRow(r.staff_id)} title="Reset adjustments">
                                            <i className="ri-refresh-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    {skippedPreviewRows.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="text-muted small">
                            <i className="ri-information-line me-1"></i>
                            Already generated / unavailable staff ({skippedPreviewRows.length})
                          </div>
                          <small className="text-muted">Existing payroll is never generated twice.</small>
                        </div>
                        <div className="table-responsive border rounded-3">
                          <table className="table table-sm align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Staff</th>
                                <th className="text-end">Base</th>
                                <th className="text-end">Net Salary</th>
                                <th className="text-end">Paid</th>
                                <th className="text-end">Pending</th>
                                <th className="text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {skippedPreviewRows.map((r) => (
                                <tr key={r.staff_id}>
                                  <td>
                                    <div className="fw-semibold">{r.staff_name}</div>
                                    <small className="text-muted">{r.skip_reason || "Not available for generation"}</small>
                                  </td>
                                  <td className="text-end">{r.already_generated ? rupee(r.base_salary) : "—"}</td>
                                  <td className="text-end fw-semibold">{r.already_generated ? rupee(r.net_salary) : "—"}</td>
                                  <td className="text-end text-success fw-semibold">{r.already_generated ? rupee(r.paid_amount) : "—"}</td>
                                  <td className="text-end text-danger fw-semibold">{r.already_generated ? rupee(r.pending_amount) : "—"}</td>
                                  <td className="text-center">
                                    {r.already_generated
                                      ? statusBadge(r.payment_status || "Generated")
                                      : <span className="badge bg-label-secondary">Skipped</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer border-top bg-white" style={{ flex: "0 0 auto" }}>
                {genStep === "pick" && (
                  <>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={closeGenerateModal}
                      disabled={genLoading}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleGeneratePreview}
                      disabled={genLoading}
                    >
                      {genLoading ? "Calculating..." : "Preview"}
                      {!genLoading && (
                        <i className="ri-arrow-right-line ms-1"></i>
                      )}
                    </button>
                  </>
                )}
                {genStep === "preview" && (
                  <>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setGenStep("pick")}
                      disabled={genLoading}
                    >
                      <i className="ri-arrow-left-line me-1"></i>Back
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleGenerateSave}
                      disabled={genLoading || selectedPayablePreviewRows.length === 0}
                    >
                      <i className="ri-save-line me-1"></i>
                      {genLoading ? "Saving..." : `Generate Selected (${selectedPayablePreviewRows.length})`}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ SALARY CALENDAR MODAL ══════════════════ */}
      {showSalaryCalendar && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              {/* HEADER */}
              <div className="modal-header salary-calendar-modal-header">
                <div className="salary-calendar-modal-title">
                  <h5 className="modal-title fw-bold mb-1">
                    <i className="ri-calendar-line me-2 text-primary"></i>
                    Salary Calendar
                  </h5>

                  {calendarStaff && (
                    <small className="text-muted d-block">
                      {calendarStaff.staff_name}
                    </small>
                  )}
                </div>

                <div className="salary-calendar-modal-actions">
                  {hasAccess("/staff/export") && (

                    <button
                      type="button"
                      className="btn btn-outline-success salary-excel-btn"
                      onClick={exportSalaryCalendarExcel}
                      disabled={monthSalaryLoading || !monthSalaryPreview}
                    >
                      <i className="ri-file-excel-2-line me-1"></i>
                      Excel
                    </button>
                  )}

                  <button
                    type="button"
                    className="salary-calendar-close-btn"
                    onClick={closeSalaryCalendar}
                    disabled={monthSalaryLoading}
                    aria-label="Close"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>

              {/* BODY */}
              <div className="modal-body">
                {/* MONTH NAVIGATION */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => changeCalendarMonth(-1)}
                    disabled={monthSalaryLoading}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>

                  <div className="text-center">
                    <h4 className="fw-bold mb-0">
                      {monthNames[calendarMonth]} {calendarYear}
                    </h4>

                    <small className="text-muted">
                      Click any date to view salary details
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => changeCalendarMonth(1)}
                    disabled={monthSalaryLoading}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>

                {/* LOADING */}
                {monthSalaryLoading && (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    ></div>

                    <div className="fw-semibold">Calculating salary...</div>

                    <small className="text-muted">
                      Checking attendance, leave, holidays, weekly offs and
                      overtime.
                    </small>
                  </div>
                )}

                {/* DATA */}
                {!monthSalaryLoading && monthSalaryPreview && (
                  <>
                    {/* MONTH SUMMARY */}
                    <div className="row g-2 mb-4">
                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">
                            Base Salary
                          </small>
                          <div className="fw-bold">
                            {rupee(monthSalaryPreview.base_salary)}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">Total Deduction</small>
                          <div className="fw-bold text-danger">
                            {rupee(monthSalaryPreview.deduction || 0)}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">
                            Working Days
                          </small>
                          <div className="fw-bold">
                            {monthSalaryPreview.working_days ??
                              monthSalaryPreview.total_working_days ??
                              0}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">Present</small>
                          <div className="fw-bold text-success">
                            {monthSalaryPreview.present_days ?? 0}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">
                            Half Days
                          </small>
                          <div className="fw-bold text-warning">
                            {monthSalaryPreview.half_days ?? 0}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">Overtime</small>
                          <div className="fw-bold text-primary">
                            {monthSalaryPreview.overtime_hours ??
                              monthSalaryPreview.overtime ??
                              0}h
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">
                            Net Salary
                          </small>
                          <div className="fw-bold text-success">
                            {rupee(monthSalaryPreview.net_salary)}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-2 col-6">
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block">
                            Incomplete Salary Paid
                          </small>
                          <div
                            className={`fw-bold ${resolveIncompleteSalaryRule(
                              monthSalaryPreview,
                              calendarPayIncompleteAttendance,
                            )
                              ? "text-success"
                              : "text-danger"
                              }`}
                          >
                            {resolveIncompleteSalaryRule(
                              monthSalaryPreview,
                              calendarPayIncompleteAttendance,
                            )
                              ? "Yes"
                              : "No"}
                          </div>
                          <small className="text-muted">
                            {resolveIncompleteSalaryRule(
                              monthSalaryPreview,
                              calendarPayIncompleteAttendance,
                            )
                              ? "Incomplete day salary included"
                              : "Incomplete day salary deducted"}
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* CALENDAR */}
                    <div className="salary-calendar">
                      {/* WEEK DAYS */}
                      <div className="salary-calendar-weekdays">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                          (day) => (
                            <div key={day} className="salary-calendar-weekday">
                              {day}
                            </div>
                          ),
                        )}
                      </div>

                      {/* DAYS */}
                      <div className="salary-calendar-grid">
                        {getCalendarDays(calendarYear, calendarMonth).map(
                          (date, index) => {
                            // Empty cell before month starts
                            if (!date) {
                              return (
                                <div
                                  key={`empty-${index}`}
                                  className="salary-calendar-empty"
                                ></div>
                              );
                            }

                            const dateString = `${calendarYear}-${String(
                              calendarMonth,
                            ).padStart(
                              2,
                              "0",
                            )}-${String(date.getDate()).padStart(2, "0")}`;

                            const dayData = (
                              monthSalaryPreview.attendance || []
                            ).find((item) => item.date === dateString);

                            const displayStatus =
                              getCalendarDisplayStatus(dayData, date);

                            const deductionAmount = Number(
                              dayData?.deduction_amount || 0,
                            );
                            const isLowTime =
                              dayData?.low_time_highlight === true ||
                              dayData?.deduction_type === "low_time" ||
                              (dayData?.deduction_type === "half_day" &&
                                Number(dayData?.low_time_minutes || 0) > 0);
                            const isLateGrace = dayData?.late_grace === true;

                            return (
                              <div
                                key={dateString}
                                className={`salary-calendar-day ${getCalendarStatusClass(
                                  displayStatus,
                                )} ${isLowTime ? "salary-calendar-low-time" : ""} ${isLateGrace ? "salary-calendar-late-grace" : ""
                                  }`}
                                onClick={() => {
                                  if (dayData) {
                                    setSelectedCalendarDay({
                                      ...dayData,
                                      status: displayStatus,
                                    });
                                  }
                                }}
                                title={dayData ? "Click to view salary details" : "No salary data"}
                                style={{
                                  cursor: dayData ? "pointer" : "default",
                                  ...(isLowTime
                                    ? {
                                      border: "2px solid #f59e0b",
                                      boxShadow: "0 0 0 2px rgba(245, 158, 11, 0.12)",
                                    }
                                    : isLateGrace
                                      ? { border: "1px dashed #f59e0b" }
                                      : {}),
                                }}
                              >
                                {/* DATE */}
                                <div className="salary-day-number">
                                  {date.getDate()}
                                </div>

                                {/* STATUS */}
                                <div className="salary-day-status">
                                  {displayStatus}
                                </div>

                                {/* ATTENDANCE TIMING - shown directly on calendar */}
                                <div
                                  className="mt-2"
                                  style={{
                                    fontSize: "11px",
                                    lineHeight: "1.45",
                                    fontWeight: 600,
                                  }}
                                >
                                  <div>
                                    <span style={{ opacity: 0.7 }}>IN&nbsp;&nbsp;</span>
                                    {formatCalendarTime(dayData?.check_in)}
                                  </div>
                                  <div>
                                    <span style={{ opacity: 0.7 }}>OUT </span>
                                    {formatCalendarTime(dayData?.check_out)}
                                  </div>
                                </div>

                                {/* Only low-time payroll information is shown here.
                                    We intentionally do not show earned/day salary. */}
                                {isLateGrace && (
                                  <div
                                    className="mt-1"
                                    style={{ fontSize: "10px", fontWeight: 700 }}
                                  >
                                    Late #{dayData?.late_occurrence || 1} • Grace
                                  </div>
                                )}

                                {isLowTime && (
                                  <div
                                    className="mt-1"
                                    style={{ fontSize: "10px", lineHeight: 1.35 }}
                                  >
                                    <div style={{ fontWeight: 700 }}>
                                      {dayData?.deduction_type === "half_day"
                                        ? "Half Day • Low Time"
                                        : "Low Time"}
                                    </div>
                                    {Number(dayData?.late_minutes || 0) > 0 && (
                                      <div>Late: {Number(dayData.late_minutes)} min</div>
                                    )}
                                    {Number(dayData?.low_time_minutes || 0) > 0 && (
                                      <div>Short: {Number(dayData.low_time_minutes)} min</div>
                                    )}
                                    {deductionAmount > 0 && (
                                      <div style={{ fontWeight: 800 }}>
                                        Deduct: {rupee(deductionAmount)}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>

                    {/* LEGEND */}
                    <div className="d-flex flex-wrap gap-3 mt-3 small">
                      <span>
                        <span className="salary-legend-box salary-calendar-present"></span>
                        Present
                      </span>

                      <span>
                        <span className="salary-legend-box salary-calendar-half"></span>
                        Half Day
                      </span>

                      <span>
                        <span className="salary-legend-box salary-calendar-absent"></span>
                        Absent
                      </span>

                      <span>
                        <span className="salary-legend-box salary-calendar-leave"></span>
                        Paid Leave
                      </span>

                      <span>
                        <span className="salary-legend-box salary-calendar-holiday"></span>
                        Holiday
                      </span>

                      <span>
                        <span className="salary-legend-box salary-calendar-weekly-off"></span>
                        Sunday / Weekly Off
                      </span>
                    </div>

                    {/* PREVIEW NOTE */}
                    <div className="alert alert-light border mt-4 mb-0 small">
                      <i className="ri-information-line me-1"></i>
                      This is a preview only. No salary or attendance record is
                      changed.
                    </div>
                  </>
                )}
              </div>

              {/* FOOTER */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeSalaryCalendar}
                  disabled={monthSalaryLoading}
                >
                  <i className="ri-close-line me-1"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ CALENDAR DAY DETAIL MODAL ══════════════════ */}
      {selectedCalendarDay && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.65)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold">
                    <i className="ri-calendar-check-line me-2 text-primary"></i>
                    Salary Details
                  </h5>

                  <small className="text-muted">
                    {selectedCalendarDay.date}
                    {selectedCalendarDay.day
                      ? ` · ${selectedCalendarDay.day}`
                      : ""}
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedCalendarDay(null)}
                ></button>
              </div>

              <div className="modal-body">
                {/* STATUS */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <small className="text-muted d-block">
                      Attendance Status
                    </small>

                    <span
                      className={`badge ${getCalendarStatusClass(
                        selectedCalendarDay.status,
                      )}`}
                    >
                      {selectedCalendarDay.status || "—"}
                    </span>
                  </div>

                  <div className="text-end">
                    <small className="text-muted d-block">Net Salary</small>

                    <h4 className="fw-bold text-success mb-0">
                      {rupee(selectedCalendarDay.net_salary)}
                    </h4>
                  </div>
                </div>

                {/* ATTENDANCE DETAILS */}
                <div className="border rounded-3 p-3 mb-3">
                  <h6 className="fw-bold mb-3">
                    <i className="ri-time-line me-2 text-primary"></i>
                    Attendance
                  </h6>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        Attendance Type
                      </small>

                      <strong>
                        {selectedCalendarDay.attendance_type || "—"}
                      </strong>
                    </div>

                    <div className="col-md-3">
                      <small className="text-muted d-block">Check In</small>

                      <strong>{selectedCalendarDay.check_in || "—"}</strong>
                    </div>

                    <div className="col-md-3">
                      <small className="text-muted d-block">Check Out</small>

                      <strong>{selectedCalendarDay.check_out || "—"}</strong>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted d-block">Worked Hours</small>

                      <strong>
                        {selectedCalendarDay.worked_hours != null
                          ? `${selectedCalendarDay.worked_hours}h`
                          : "—"}
                      </strong>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        Required Hours
                      </small>

                      <strong>
                        {selectedCalendarDay.required_hours != null
                          ? `${selectedCalendarDay.required_hours}h`
                          : "—"}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* SALARY DETAILS */}
                <div className="border rounded-3 p-3">
                  <h6 className="fw-bold mb-3">
                    <i className="ri-money-rupee-circle-line me-2 text-success"></i>
                    Salary Calculation
                  </h6>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <small className="text-muted d-block">
                        Earned Salary
                      </small>

                      <strong>
                        {rupee(selectedCalendarDay.salary_earned)}
                      </strong>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted d-block">Overtime</small>

                      <strong className="text-primary">
                        {selectedCalendarDay.overtime_hrs
                          ? `${selectedCalendarDay.overtime_hrs}h`
                          : "0h"}
                      </strong>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted d-block">OT Bonus</small>

                      <strong className="text-primary">
                        {rupee(selectedCalendarDay.overtime_bonus)}
                      </strong>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted d-block">Late Mark</small>
                      <strong className={Number(selectedCalendarDay.late_minutes || 0) > 0 ? "text-warning" : ""}>
                        {selectedCalendarDay.late_minutes || 0} min
                      </strong>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted d-block">Low Time</small>
                      <strong>{selectedCalendarDay.low_time_minutes || 0} min</strong>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted d-block">Deduction</small>
                      <strong className="text-danger">{rupee(selectedCalendarDay.deduction_amount || 0)}</strong>
                    </div>
                    {selectedCalendarDay.deduction_reason && (
                      <div className="col-12">
                        <div className="alert alert-warning py-2 mb-0 small">
                          <strong>Deduction reason:</strong> {selectedCalendarDay.deduction_reason}
                        </div>
                      </div>
                    )}

                    <div className="col-12">
                      <hr className="my-1" />
                    </div>

                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">
                          Net Salary for this Date
                        </span>

                        <span className="fw-bold fs-5 text-success">
                          {rupee(selectedCalendarDay.net_salary)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedCalendarDay(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ══════════════ BULK SALARY PAYMENT MODAL ══════════════ */}
      {bulkPayOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold mb-1">
                    <i className="ri-money-rupee-circle-line text-success me-2"></i>
                    Bulk Salary Payment — {monthNames[Number(month)]} {year}
                  </h5>
                  <small className="text-muted">Generated pending salaries only. Verify amount and uncheck any staff you want to skip.</small>
                </div>
                <button type="button" className="btn-close" disabled={bulkPayLoading} onClick={() => setBulkPayOpen(false)}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Debit Bank Account</label>
                    <select className="form-select" value={bulkPayForm.debit_account} onChange={(e) => setBulkPayForm((p) => ({ ...p, debit_account: e.target.value }))}>
                      <option value="">Select Bank Account</option>
                      {bulkBanks.map((bank) => (
                        <option key={bank.id || bank.account_no} value={bank.account_no}>
                          A/C: {bank.account_no} — Bal: {rupee(bank.amount)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Payment Date</label>
                    <input type="date" className="form-control" value={bulkPayForm.payment_date} onChange={(e) => setBulkPayForm((p) => ({ ...p, payment_date: e.target.value }))} />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Remark</label>
                    <input type="text" className="form-control" placeholder="Optional bulk salary payment remark" value={bulkPayForm.remark} onChange={(e) => setBulkPayForm((p) => ({ ...p, remark: e.target.value }))} />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                  <div>
                    <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={toggleAllBulkSalary}>
                      {bulkSelectedIds.length === bulkPayableRows.length ? "Clear All" : "Select All Pending"}
                    </button>
                    <span className="small text-muted">{bulkSelectedIds.length} of {bulkPayableRows.length} selected</span>
                  </div>
                  <div className="fw-bold">Selected Total: <span className="text-success">{rupee(bulkTotal)}</span></div>
                </div>

                <div className="table-responsive border rounded">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 50 }} className="text-center">Select</th>
                        <th>Staff</th>
                        <th className="text-end">Generated Salary</th>
                        <th className="text-end">Already Paid</th>
                        <th className="text-end">Pending</th>
                        <th style={{ width: 190 }}>Pay Amount</th>
                        <th className="text-center">After Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPayableRows.map((row) => {
                        const salaryId = Number(row.salary_id);
                        const selected = bulkSelectedIds.includes(salaryId);
                        const net = Number(row.month_net_salary ?? row.total_earned ?? 0);
                        const paid = Number(row.month_paid_amount ?? row.total_paid ?? 0);
                        const pending = Number(row.month_pending_amount ?? row.total_pending ?? 0);
                        const payAmount = Number(bulkAmounts[row.salary_id] || 0);
                        const afterPending = Math.max(0, pending - payAmount);
                        return (
                          <tr key={salaryId} className={!selected ? "opacity-50" : ""}>
                            <td className="text-center">
                              <input className="form-check-input" type="checkbox" checked={selected} onChange={() => toggleBulkSalary(salaryId)} />
                            </td>
                            <td>
                              <div className="fw-semibold">{row.name}</div>
                              <small className="text-muted">{row.registration_no || ""}</small>
                            </td>
                            <td className="text-end fw-semibold">{rupee(net)}</td>
                            <td className="text-end text-success">{rupee(paid)}</td>
                            <td className="text-end text-danger fw-semibold">{rupee(pending)}</td>
                            <td>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">₹</span>
                                <input type="number" min="0.01" max={pending} step="0.01" className="form-control text-end" disabled={!selected} value={bulkAmounts[row.salary_id] ?? ""} onChange={(e) => changeBulkAmount(row, e.target.value)} />
                              </div>
                            </td>
                            <td className="text-center">
                              {afterPending <= 0 ? <span className="badge bg-label-success">Paid</span> : <span className="badge bg-label-warning">Partial · {rupee(afterPending)}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer d-flex justify-content-between">
                <div className="small text-muted">Only checked staff will be paid. Existing one-by-one payment remains unchanged.</div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" disabled={bulkPayLoading} onClick={() => setBulkPayOpen(false)}>Cancel</button>
                  <button type="button" className="btn btn-success" disabled={bulkPayLoading || selectedBulkRows.length === 0 || bulkTotal <= 0} onClick={submitBulkPay}>
                    {bulkPayLoading ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="ri-check-double-line me-1"></i>Pay Selected {rupee(bulkTotal)}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
